import { useEffect, useMemo, useReducer, useRef, useState } from 'preact/hooks';
import {
  WidgetApi,
  WidgetApiError,
  checkoutRecoveryMessage,
  isCardStateMismatch,
  recoveryBanner,
} from './api';
import { type DeepLinkParams, hasAnyDeepLinkParams } from './deep-link';
import { pushDataLayer } from './datalayer';
import { emit } from './emitter';
import { captureAttribution, ensureFireTrack, identify, readFtVisitorId, track } from './firetrack';
import { ConfirmStep } from './steps/Confirm';
import { ContactStep } from './steps/Contact';
import { DoneStep } from './steps/Done';
import { EnhanceStep } from './steps/Enhance';
import { PaymentStep } from './steps/Payment';
import { StripePaymentElement } from './steps/StripePaymentElement';
import { DepositHeader, DepositSummary } from './components/DepositSummary';
import { FinancingMessage } from './components/FinancingMessage';
import { ServiceStep } from './steps/Service';
import { BeYouServiceView } from './steps/BeYouView';
import { PurchaseFlow } from './steps/Purchase';
import { ServiceConfigurator } from './steps/ServiceConfigurator';
import { StaffStep } from './steps/Staff';
import { TimeStep } from './steps/Time';
import { Chrome } from './components/Chrome';
import { RunningBar } from './components/RunningBar';
import { isRequiredAddOnGateSatisfied } from './lib/addon-gate';
import { onHoldExpiry } from './lib/payment-lock';
import { paymentStepNeeded } from './lib/payment-step';
import { checkoutResumedOnce, pendingResumeToken, redeemOnce } from './lib/resume';
import { payOverTimeProviders } from './lib/financing-copy';
import {
  cartIsSensitive,
  isSensitive,
  SENSITIVE_SERVICE_LABEL,
  serviceAnalyticsFields,
} from './lib/analytics-privacy';
import { HoldStrip } from './components/HoldStrip';
import { initialState, reducer } from './state';
import type { Booking, BootstrapResponse, Cart, ShopProduct } from './types';

interface AppProps {
  merchant: string;
  apiBase: string;
  deepLink?: DeepLinkParams;
  // Phase 7 Commit H. Widget slug from the embed snippet (or null for
  // the tenant's default widget). Forwarded to the bootstrap call so
  // the server resolves the right Widget row's overrides.
  widgetSlug?: string | null;
  // Tags every emitter event with the session that fired it. Caller
  // generates one per mount so consumers can correlate events with
  // their own SalonLift.open(...) call. Defaults to a fresh uuid if
  // the caller doesn't pass one (covers auto-mount + tests).
  sessionId?: string;
}

/**
 * UUID v4 for idempotency key (used on checkout).
 *
 * Prefers crypto.randomUUID (all modern browsers). Falls back to building
 * one from crypto.getRandomValues — still cryptographically secure, just
 * older API surface. Math.random is a last-resort safety net that should
 * never realistically fire (every browser since 2017 supports one of the
 * above).
 */
function uuid(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = (globalThis as any).crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    // RFC 4122 v4: bits 6-7 of byte 8 = 10, bits 4-7 of byte 6 = 0100
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-resort insecure fallback. Log loudly so we notice if it ever fires.
  // eslint-disable-next-line no-console
  console.warn('[sb-widget] crypto unavailable — falling back to Math.random UUID');
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Perceived luminance (BT.601). 0 = black, 1 = white. Accurate enough
 * for UI contrast picks; proper WCAG sRGB-linear is overkill here.
 */
function perceivedLuminance(hex: string): number {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return 0;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Pick a foreground "ink" color for a given background hex so type
 * stays readable. Threshold 0.6 keeps warm earthy tones (terracotta
 * `#c8765a`, orange `#d97706`) on white — they look bad with
 * black-on-orange — and only flips genuinely-light brands (yellow
 * `#fbbf24`, pastels, near-white) to dark ink.
 */
function pickInkForBg(hex: string): string {
  return perceivedLuminance(hex) > 0.6 ? '#0a0a0a' : '#ffffff';
}

/**
 * Brand color used as foreground TEXT (not button background). Light
 * brands like yellow are unreadable as text on cream/white surfaces —
 * darken them by mixing toward black until they have enough contrast.
 * Dark brands pass through unchanged.
 */
function pickBrandFg(hex: string): string {
  const lum = perceivedLuminance(hex);
  if (lum <= 0.55) return hex;
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Mix more toward black the lighter the brand is. Lum 0.55 → 35%
  // black mix; lum 1.0 → 65% black mix. Keeps the hue, kills the
  // luminance enough for text-on-cream to be legible.
  const mix = 0.35 + (lum - 0.55) * 0.67;
  const r2 = Math.max(0, Math.round(r * (1 - mix)));
  const g2 = Math.max(0, Math.round(g * (1 - mix)));
  const b2 = Math.max(0, Math.round(b * (1 - mix)));
  return `#${r2.toString(16).padStart(2, '0')}${g2.toString(16).padStart(2, '0')}${b2.toString(16).padStart(2, '0')}`;
}

export function App({ merchant, apiBase, deepLink, widgetSlug, sessionId }: AppProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Bumping this re-runs the mount effect, which re-fetches bootstrap and
  // re-creates the cart. Used by the cart-init retry below: re-running the
  // whole effect (rather than extracting just the createCart call) means the
  // PHD auto-add and deep-link preselect paths recover identically, with no
  // duplicated logic to drift.
  const [retryNonce, setRetryNonce] = useState(0);
  /**
   * The promo code string, for the label only — the cart carries the
   * discount AMOUNT, which is what actually matters. Held here rather
   * than on the cart so echoing a label back does not need a schema
   * change; a fresh cart load simply shows "Promo code applied".
   */
  const [promoLabel, setPromoLabel] = useState<string | null>(null);

  // Every post-SERVICE handler needs a cart. Without one they used to `return`
  // silently: the click did nothing — no request, no error, no spinner, no
  // message — and because the cart is created once at mount with no retry
  // path, it did nothing *forever*. Say something instead. SET_ERROR also
  // resets `loading`, so this can never leave a stuck spinner.
  function failNoCart() {
    dispatch({
      type: 'SET_ERROR',
      value: 'We lost your booking session — please reload the page and start again.',
    });
  }
  // True-lazy refs — useRef(expr) evaluates `expr` on every render and
  // discards the result after the first; the &&-init pattern only ever
  // calls the factory once. captureAttribution walks window.location +
  // cookie regexes; WidgetApi allocates an instance — both avoidable on
  // every re-render in this hot widget.
  const apiRef = useRef<WidgetApi | null>(null);
  if (!apiRef.current) apiRef.current = new WidgetApi(apiBase, merchant);
  const api = apiRef.current;
  const attributionRef = useRef<ReturnType<typeof captureAttribution> | null>(null);
  if (!attributionRef.current) attributionRef.current = captureAttribution();
  const attribution = attributionRef.current;

  /**
   * Phase 1c — attribution + FireTrack visitor cookie in the shape the
   * server takes, so it persists on Cart and the booking.created webhook
   * copies it onto Booking. The dispatcher reads it back at fire time.
   * Sent with checkout AND with the deferred payment intent: a redirect
   * payment may be finished by the Stripe webhook, with no browser left
   * to send it. Undefined when there is nothing to send.
   */
  function serverAttribution():
    | {
        clickIds?: Record<string, string>;
        utms?: Record<string, string>;
        landingPageUrl?: string;
        referrerUrl?: string;
        firetrackVisitorId?: string;
      }
    | undefined {
    const attr = attribution as {
      utms?: Record<string, string>;
      clickIds?: Record<string, string>;
      landingPage?: string;
      referrer?: string;
    };
    const out = {
      ...(attr.clickIds && Object.keys(attr.clickIds).length > 0
        ? { clickIds: attr.clickIds }
        : {}),
      ...(attr.utms && Object.keys(attr.utms).length > 0 ? { utms: attr.utms } : {}),
      ...(attr.landingPage ? { landingPageUrl: attr.landingPage } : {}),
      ...(attr.referrer ? { referrerUrl: attr.referrer } : {}),
      ...((): { firetrackVisitorId?: string } => {
        const v = readFtVisitorId();
        return v ? { firetrackVisitorId: v } : {};
      })(),
    };
    return Object.keys(out).length > 0 ? out : undefined;
  }
  const idempotencyKeyRef = useRef<string | null>(null);
  if (!idempotencyKeyRef.current) idempotencyKeyRef.current = uuid();
  const idempotencyKey = idempotencyKeyRef.current;
  // From main (imperative API): session-id pinned for the lifetime of
  // this widget mount. Lazy-init in the same style as the other refs.
  const sidRef = useRef<string | null>(null);
  // P5.2 D — a cart resumed from a payment redirect, waiting for the
  // bootstrap to land so checkout can run (confirmBooking needs it).
  const resumeCheckoutRef = useRef<Cart | null>(null);
  // P5.2 D — a resumed cart's booking is running; the payment step shows
  // "finishing" over a form the customer must not use meanwhile.
  const [finishingPayment, setFinishingPayment] = useState(false);
  // Phase 6 — a Special Service being bought (no appointment). While set,
  // the purchase flow replaces the booking steps; the cart is untouched.
  const [purchase, setPurchase] = useState<{ product: ShopProduct; label: string } | null>(null);
  const [beYouSelectedProductId, setBeYouSelectedProductId] = useState<string | null>(null);
  const productLinkRef = useRef(false);
  // A Payment Element confirm is in flight: the amount must not move under
  // it, so the pay-in-full choice and the promo code lock.
  const [paymentConfirming, setPaymentConfirming] = useState(false);
  // …and from the moment Pay is pressed, before Stripe has validated.
  const [paymentBusy, setPaymentBusy] = useState(false);
  // Payment Element step only: a change to the total is in flight, or its
  // refresh failed. Pay waits on the first and stays blocked on the
  // second, so nobody pays an amount the server never confirmed. Kept
  // HERE, around the shared promo / pay-mode handlers, rather than inside
  // them — those also serve the Card Element step every other tenant
  // uses (H22).
  const [peQuotePending, setPeQuotePending] = useState(false);
  const [peQuoteFailed, setPeQuoteFailed] = useState(false);
  async function guardQuoteChange<T>(change: () => Promise<T>): Promise<T> {
    setPeQuotePending(true);
    try {
      const result = await change();
      setPeQuoteFailed(false);
      return result;
    } catch (err) {
      setPeQuoteFailed(true);
      throw err;
    } finally {
      setPeQuotePending(false);
    }
  }
  if (!sidRef.current) sidRef.current = sessionId ?? uuid();
  const sid = sidRef.current;
  const dlRef = useRef<{ value: DeepLinkParams | undefined } | null>(null);
  if (!dlRef.current) dlRef.current = { value: deepLink };
  const dl = dlRef.current.value;
  // Phase 5. The bootstrap stale-while-revalidate callback is created
  // inside the mount-time useEffect and closes over the step at that
  // moment ('SERVICE'). We need to read the current step at refresh
  // time, so mirror state.step into a ref the closure can dereference.
  const stepRef = useRef(state.step);
  useEffect(() => {
    stepRef.current = state.step;
  }, [state.step]);
  // D56 — banner shown above the active step when a preselect fell back
  // (e.g. ?service=svc_id_that_no_longer_exists). Auto-dismisses on the
  // next step transition so it doesn't haunt the rest of the flow.
  const [preselectNotice, setPreselectNotice] = useState<string | null>(null);

  // Auto-scroll the error banner into view when a new error fires —
  // covers the case where the user hit "Book my appointment" from the
  // bottom of the Confirm step and the resulting banner was below
  // their current scroll position. scrollIntoView with block:'start'
  // brings the banner to the top of the visible area inside the
  // shadow root's scroll container.
  useEffect(() => {
    if (!state.error || state.step === 'DONE') return;
    // Defer one frame so the DOM has the banner mounted before we ask
    // to scroll to it.
    const raf = requestAnimationFrame(() => {
      const banner =
        typeof document !== 'undefined'
          ? document
              .querySelector('[data-sb-widget]')
              ?.shadowRoot?.querySelector('.sbw-banner-error')
          : null;
      banner?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [state.error, state.step]);

  // Bootstrap on mount
  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'SET_LOADING', value: true });
    api
      // Phase 7 Commit H. Widget slug resolution priority:
      //   1. `widgetSlug` prop from the embed snippet's data-widget-slug
      //   2. `?w=` (or legacy `?theme=`) deep-link param from the URL
      //   3. server falls back to tenant's default widget
      .bootstrap({
        widget: widgetSlug ?? dl?.themeSlug ?? null,
        // Deep-linked service (URL `?service=` OR programmatic
        // `SalonLift.open({ service })`). Forwarded so the server can
        // append an unlisted service; also partitions the bootstrap cache.
        preselect: dl?.preselectedServiceId ?? null,
        // Phase 5. Stale-while-revalidate: serve the cached catalog
        // immediately, then refresh in the background. If the refresh
        // returns a meaningfully-different catalog AND the user hasn't
        // started building a cart yet, hot-swap so they see the latest
        // services. Mid-flow refreshes (post-SERVICE) are suppressed —
        // yanking services out from under a cart-in-progress would be
        // worse than showing stale data for one session.
        onRefresh: (fresh) => {
          if (cancelled) return;
          if (stepRef.current === 'SERVICE') {
            dispatch({ type: 'BOOTSTRAP_LOADED', value: fresh });
          } else {
            // The pay-over-time names are copy, not catalogue — they are
            // still safe to swap mid-cart (P5.3).
            dispatch({ type: 'FINANCING_REFRESHED', methods: fresh.financingMethods });
            // eslint-disable-next-line no-console
            console.info('[SalonLift] bootstrap refresh suppressed — user is past SERVICE step');
          }
        },
      })
      .then(async (b) => {
        if (cancelled) return;
        dispatch({ type: 'BOOTSTRAP_LOADED', value: b });
        // Init FireTrack if tenant has a project configured
        if (b.firetrack?.projectKey && b.firetrack?.scriptUrl) {
          ensureFireTrack(b.firetrack.projectKey, b.firetrack.scriptUrl);
        }
        track('widget_viewed', { merchant, metadata: attribution });
        pushDataLayer('widget_viewed', { merchant, metadata: attribution });
        // Funnel-event spec name (booking_widget_loaded) fires alongside
        // the legacy widget_viewed so existing dashboards keep working
        // while GA4 + Google Ads tags read from the new name.
        track('booking_widget_loaded', { merchant, metadata: attribution });
        pushDataLayer('booking_widget_loaded', { merchant, metadata: attribution });
        emit({
          type: 'widget_ready',
          merchant,
          widgetSlug: widgetSlug ?? null,
          sessionId: sid,
        });
        // Create cart eagerly so widget state persists across reloads.
        //
        // Both failure paths below used to leave the widget SILENTLY DEAD:
        // BOOTSTRAP_LOADED has already dispatched, so the catalogue renders and
        // every button looks live — but the cart is created exactly once, here,
        // with no retry path and no CART_CLEARED action. A bare `return` (or a
        // throw routed to the generic .catch, whose banner the next STEP_SET
        // wipes) left `cart: null` for the rest of the session, and every
        // handler downstream hit `if (!state.cart) return;` and did nothing.
        // The customer sees a perfectly healthy widget that ignores them.
        if (!b.location) {
          dispatch({
            type: 'CART_INIT_FAILED',
            value: "Online booking isn't available for this salon right now. Please call to book.",
          });
          return;
        }
        // P5.2 D — back from a payment redirect (Klarna, Affirm, a bank)
        // with a resume token: THEIR cart, not a new one. The money has
        // already moved (or is moving); the ordinary checkout books on it,
        // or returns the booking the Stripe webhook already made. Shared
        // per page load, so a second widget instance gets the same cart
        // instead of a refusal for the spent token. Refused or absent →
        // carry on as a fresh visit below.
        if (pendingResumeToken()) {
          const resumed = await redeemOnce(merchant, (t) => api.resumeCart(t));
          if (cancelled) return;
          if (resumed?.cart) {
            dispatch({ type: 'CART_SET', value: resumed.cart });
            const first = resumed.cart.lines[0];
            if (first?.serviceId) dispatch({ type: 'SERVICE_SELECTED', serviceId: first.serviceId });
            if (first?.staffId) dispatch({ type: 'STAFF_SELECTED', staffId: first.staffId });
            try {
              const quote = await api.getDeposit(resumed.cart.id);
              if (!cancelled) dispatch({ type: 'DEPOSIT_LOADED', deposit: quote });
            } catch {
              // Checkout decides on the money from the server's own
              // records; the quote only draws the payment step behind the
              // "finishing" notice.
            }
            if (cancelled) return;
            // Back on the PAYMENT step, not Confirm: they have just paid
            // (or tried to), and a "book my appointment" screen after that
            // reads as "not booked yet" (Be You, 2026-10-12). The checkout
            // effect books it — success → Done, a payment that did not go
            // through → this same step with the reason.
            resumeCheckoutRef.current = resumed.cart;
            dispatch({ type: 'STEP_SET', value: 'PAYMENT' });
            dispatch({ type: 'SET_LOADING', value: true });
            setFinishingPayment(true);
            return;
          }
        }

        let cart;
        try {
          cart = await api.createCart(b.location.id);
        } catch (e) {
          if (cancelled) return;
          dispatch({
            type: 'CART_INIT_FAILED',
            value: e instanceof Error ? e.message : String(e),
          });
          return;
        }
        if (cancelled) return;
        dispatch({ type: 'CART_SET', value: cart });

        // PHD Studio variant — single-calendar, contact-first flow.
        // Auto-add the only service to cart and jump straight to CONTACT;
        // the rest of the flow (Contact → Time → Payment) is driven by
        // variant-aware transitions in submitContact + commitTime below.
        // Skipping the deep-link block entirely since there's only one
        // service and nothing to preselect.
        if (b.branding?.customViewBundle === 'phd-studio' && b.services[0]) {
          const svc = b.services[0];
          const updated = await api.addService(cart.id, { serviceId: svc.id });
          if (cancelled) return;
          dispatch({ type: 'CART_SET', value: updated });
          dispatch({ type: 'SERVICE_SELECTED', serviceId: svc.id });
          // Any-staff booking — there's only one stylist anyway, so we
          // don't need a staff step (mirrors bypassStaffSelection).
          dispatch({ type: 'STAFF_SELECTED', staffId: null });
          dispatch({ type: 'STEP_SET', value: 'CONTACT' });

          // Background-prefetch the date strip while the user fills out
          // Contact. GHL's free-slots takes several seconds against a
          // 60-day window; without this the user lands on TIME and waits
          // on an empty strip. The prefetch runs alongside human typing
          // time, so dates are almost always ready by the time Contact
          // submits. submitContact checks state.availableDates before
          // firing its own fetch — if this prefetch already populated
          // the strip, the post-submit path skips the duplicate call.
          const tz = b.location.timezone ?? 'UTC';
          const today = new Date().toISOString().slice(0, 10);
          const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
          void api
            .availableDates(updated.id, today, end, tz)
            .then(({ dates }) => {
              if (cancelled) return;
              dispatch({ type: 'DATES_LOADED', dates });
            })
            .catch(() => {
              // Prefetch is best-effort. submitContact will retry if
              // state.availableDates is still empty when the user lands
              // on TIME, and the Time step's loading copy covers the
              // worst case.
            });
          return;
        }

        // ----- D56: deep-link preselect application --------------------
        if (!dl || !hasAnyDeepLinkParams(dl)) return;
        // Phase 6 — a link to a Special Service opens its purchase (the
        // effect below); a booking preselect on the same link must not
        // start a cart underneath it. An unknown product falls through.
        if (dl.productId && b.shop?.products.some((p) => p.id === dl.productId)) return;

        // Category filter (Phase 7+). Resolves to a custom WidgetServiceGroup
        // first (UUIDs from our DB), then a Square category id. Service
        // preselect takes priority — picking a service implies a category,
        // and dropping into Stylist beats showing the filtered menu.
        //
        // For BeYou-variant tenants the category lives in the hardcoded
        // BEYOU_CATEGORIES (e.g. 'skin-club') and is consumed via the
        // beYouActiveTab init above — resolveCategoryFilter would fail
        // for those slugs, so skip the menu-updated notice when we know
        // BeYou will pick it up.
        const beYouVariant = b.branding?.customViewBundle === 'be-you';
        if (dl.categoryId && !dl.preselectedServiceId) {
          const filter = resolveCategoryFilterList(b, dl.categoryId);
          if (filter) {
            dispatch({ type: 'CATEGORY_FILTER_SET', filter });
            track('deep_link_category_applied', {
              category_id: dl.categoryId,
              metadata: attribution,
            });
          } else if (!beYouVariant) {
            // Non-BeYou tenant landed with an unknown category — show
            // the menu-updated notice.
            setPreselectNotice("We've updated our menu — please pick from current services.");
            track('deep_link_preselect_dropped', {
              kind: 'category',
              metadata: attribution,
            });
          }
        }

        // Service preselect: only honor IDs that actually exist in the
        // catalog. Bootstrap already filters by visibility — a deep-link
        // ID hidden by the operator falls back per their intent.
        if (dl.preselectedServiceId) {
          const svc = b.services.find((s) => s.id === dl.preselectedServiceId);
          if (svc) {
            await applyServicePreselect(svc.id, b, cart, dl, cancelled);
          } else {
            setPreselectNotice("We've updated our menu — please pick from current services.");
            track('deep_link_preselect_dropped', {
              kind: 'service',
              metadata: attribution,
            });
          }
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof WidgetApiError ? e.message : String(e);
        dispatch({ type: 'SET_ERROR', value: msg });
      });
    return () => {
      cancelled = true;
    };
    // retryNonce: bumping it re-runs bootstrap + cart creation wholesale, so
    // the cart-init retry recovers the PHD and deep-link paths too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryNonce]);

  // Phase 7+. Resolve `?category=` to a snapshot the Service step can
  // render. Tries custom WidgetServiceGroup first (UUIDs from our DB),
  // then falls back to Square category id. Returns null when neither
  // matches — caller surfaces the "menu updated" banner.
  // Multi-category deep links (2026-07-16, Odessa "cuts + styling" ask):
  // `?category=` accepts a comma-separated id list. Each id resolves
  // independently (custom group first, then provider category); the
  // matches merge into one filter whose name joins the resolved names
  // ("Haircuts & Styling") and whose serviceIds union the members. As
  // long as at least one id resolves the filter applies — only a fully
  // unknown list surfaces the menu-updated banner.
  function resolveCategoryFilterList(
    bootstrap: NonNullable<typeof state.bootstrap>,
    rawParam: string,
  ) {
    const ids = rawParam
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
    const resolved = ids
      .map((id) => resolveCategoryFilter(bootstrap, id))
      .filter((f): f is NonNullable<typeof f> => f !== null);
    if (resolved.length === 0) return null;
    if (resolved.length === 1) return resolved[0]!;
    return {
      id: resolved.map((f) => f.id).join(','),
      name: resolved.map((f) => f.name).join(' & '),
      serviceIds: [...new Set(resolved.flatMap((f) => f.serviceIds))],
    };
  }

  function resolveCategoryFilter(
    bootstrap: NonNullable<typeof state.bootstrap>,
    categoryId: string,
  ) {
    const customGroup = bootstrap.serviceGroups?.find((g) => g.id === categoryId);
    if (customGroup) {
      return {
        id: customGroup.id,
        name: customGroup.name,
        serviceIds: customGroup.services.map((s) => s.id),
      };
    }
    const squareCat = bootstrap.squareCategories?.find((c) => c.id === categoryId);
    if (squareCat) {
      return {
        id: squareCat.id,
        name: squareCat.name,
        serviceIds: squareCat.serviceIds,
      };
    }
    return null;
  }

  async function applyServicePreselect(
    serviceId: string,
    bootstrap: NonNullable<typeof state.bootstrap>,
    cart: NonNullable<typeof state.cart>,
    dl: DeepLinkParams,
    cancelled: boolean,
  ) {
    try {
      dispatch({ type: 'SERVICE_SELECTED', serviceId });
      const service = bootstrap.services.find((s) => s.id === serviceId);
      const dlServicePayload = {
        ...serviceAnalyticsFields(serviceId, service, { withValue: true }),
        metadata: { ...attribution, source: 'deep_link' },
      };
      track('service_selected', dlServicePayload);
      pushDataLayer('service_selected', dlServicePayload);
      emit({ type: 'booking_started', serviceId, sessionId: sid });
      // Add to cart with optional staff preselect
      const stafFromDl =
        dl.preselectedStaffId && bootstrap.staff.some((t) => t.id === dl.preselectedStaffId)
          ? dl.preselectedStaffId
          : null;
      const bypassStaff = bootstrap.behavior?.bypassStaffSelection ?? false;
      const updated = await api.addService(cart.id, {
        serviceId,
        ...(stafFromDl ? { staffId: stafFromDl } : {}),
        // An unlisted (deep-link-only) service is non-online-bookable in the
        // provider; allow it past the add guard since it was reached by an
        // explicit direct link.
        ...(service?.hidden && service.unlistedToken
          ? { unlistedToken: service.unlistedToken }
          : {}),
      });
      if (cancelled) return;
      dispatch({ type: 'CART_SET', value: updated });

      // D55b — when the chosen base service has upsells, route to ENHANCE
      // first regardless of staff preselect. The Enhance step's Continue
      // handler will honor whatever staff context exists. Without this
      // gate, a deep-linked service with required modifiers would silently
      // skip the modifier picker.
      const upsells = bootstrap.upsellsByService?.[serviceId];
      const hasUpsells = (upsells?.addOns.length ?? 0) > 0 || (upsells?.modifiers.length ?? 0) > 0;
      if (hasUpsells) {
        if (stafFromDl) {
          dispatch({ type: 'STAFF_SELECTED', staffId: stafFromDl });
        }
        dispatch({ type: 'STEP_SET', value: 'ENHANCE' });
        return;
      }

      // If staff is preselected (or bypass is on, or the service has a
      // single eligible stylist), advance straight to TIME and load dates.
      // Otherwise advance to STAFF.
      const dlSolo =
        service &&
        service.staffIds.length === 1 &&
        bootstrap.staff.some((t) => t.id === service.staffIds[0])
          ? service.staffIds[0]!
          : null;
      if (stafFromDl || bypassStaff || dlSolo) {
        dispatch({
          type: 'STAFF_SELECTED',
          staffId: stafFromDl ?? (bypassStaff ? null : dlSolo),
        });
        dispatch({ type: 'STEP_SET', value: 'TIME' });
        const tz = bootstrap.location?.timezone ?? 'UTC';
        const today = new Date().toISOString().slice(0, 10);
        const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
        const { dates } = await api.availableDates(updated.id, today, end, tz);
        if (!cancelled) dispatch({ type: 'DATES_LOADED', dates });
      } else {
        dispatch({ type: 'STEP_SET', value: 'STAFF' });
        // If the operator passed ?staff=stf_id_unknown we silently drop
        // it and let them pick — staff IDs change less often than service
        // IDs and the inline error wouldn't be actionable.
        if (dl.preselectedStaffId && !stafFromDl) {
          track('deep_link_preselect_dropped', {
            kind: 'staff',
            metadata: attribution,
          });
        }
      }
    } catch (e) {
      if (cancelled) return;
      const msg = e instanceof Error ? e.message : String(e);
      dispatch({ type: 'SET_ERROR', value: msg });
    }
  }

  // Phase 7 Commit J. Tap a service card to HIGHLIGHT (not commit). The
  // RunningBar's Continue button does the cart-add + advance via
  // commitService() below. Anchors the running-total UX without forcing
  // a network round-trip on every tap.
  function selectService(serviceId: string) {
    dispatch({ type: 'SERVICE_SELECTED', serviceId });
    const service = state.bootstrap?.services.find((s) => s.id === serviceId);
    const payload = {
      ...serviceAnalyticsFields(serviceId, service),
      metadata: attribution,
    };
    track('service_highlighted', payload);
    pushDataLayer('service_highlighted', payload);
  }

  // Phase 7 Commit J. Commits the highlighted service: tears down any
  // previous cart line, adds the new one, fires the conversion event,
  // and routes past Service.
  async function commitService() {
    if (!state.cart) {
      failNoCart();
      return;
    }
    if (!state.selectedServiceId) return;
    const serviceId = state.selectedServiceId;
    const service = state.bootstrap?.services.find((s) => s.id === serviceId);
    const payload = {
      ...serviceAnalyticsFields(serviceId, service, { withValue: true }),
      metadata: attribution,
    };
    track('service_selected', payload);
    pushDataLayer('service_selected', payload);
    emit({ type: 'booking_started', serviceId, sessionId: sid });
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      let cart = state.cart;
      // Snapshot BEFORE teardown so a rejected new service can be unwound
      // instead of leaving the customer with an empty cart.
      const snapshot = cart.lines.map((l) => ({
        serviceId: l.serviceId,
        ...(l.staffId ? { staffId: l.staffId } : {}),
      }));
      try {
        for (const existing of cart.lines) {
          cart = await api.removeService(cart.id, existing.lineId);
        }
        // A deep-linked hidden service can be re-committed here (e.g. Back
        // from Staff, then Continue) — keep its unlisted override or the
        // provider's add guard rejects the re-add.
        cart = await api.addService(cart.id, {
          serviceId,
          ...(service?.hidden && service.unlistedToken
            ? { unlistedToken: service.unlistedToken }
            : {}),
        });
      } catch (swapErr) {
        cart = await restoreCartLines(cart, snapshot);
        dispatch({ type: 'CART_SET', value: cart });
        throw swapErr;
      }
      dispatch({ type: 'CART_SET', value: cart });

      await advancePastService(serviceId, cart);
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  // Routes the user past the Service step. Inserts ENHANCE when the chosen
  // base service has any configured add-ons or modifiers (D55b); otherwise
  // honors the existing bypassStaff preference.
  async function advancePastService(serviceId: string, cart: NonNullable<typeof state.cart>) {
    const upsells = state.bootstrap?.upsellsByService?.[serviceId];
    const hasUpsells = (upsells?.addOns.length ?? 0) > 0 || (upsells?.modifiers.length ?? 0) > 0;
    if (hasUpsells) {
      dispatch({ type: 'STEP_SET', value: 'ENHANCE' });
      return;
    }
    await advancePastEnhance(cart);
  }

  // Single-eligible-stylist detection: a Staff step with exactly one
  // option is pure friction (Odessa's Extensions Consultation — one
  // stylist performs it), so the widget pins that stylist and skips the
  // step. Pinning (vs any-staff null) keeps availability scoped to the
  // right calendar and the "with {name}" copy exact. Services with EMPTY
  // staffIds (providers that don't ship eligibility, e.g. Meevo Phase 1)
  // are untouched. The Time step's Back button mirrors this via
  // staffStepSkippedFor().
  function singleEligibleStaffId(serviceId: string | null | undefined): string | null {
    if (!serviceId) return null;
    const svc = state.bootstrap?.services.find((x) => x.id === serviceId);
    if (!svc || svc.staffIds.length !== 1) return null;
    const only = svc.staffIds[0]!;
    return state.bootstrap?.staff.some((t) => t.id === only) ? only : null;
  }

  function staffStepSkippedFor(serviceId: string | null | undefined): boolean {
    return (
      (state.bootstrap?.behavior?.bypassStaffSelection ?? false) ||
      singleEligibleStaffId(serviceId) !== null
    );
  }

  // Routes from ENHANCE (or directly past it) into STAFF / TIME based on
  // the bypassStaff toggle or a single-eligible-stylist service. Cart is
  // whatever it currently is — caller is responsible for any add-on
  // insertion before calling.
  async function advancePastEnhance(cart: NonNullable<typeof state.cart>) {
    const bypassStaff = state.bootstrap?.behavior?.bypassStaffSelection ?? false;
    const soloStaff = singleEligibleStaffId(state.selectedServiceId);
    if (bypassStaff || soloStaff) {
      dispatch({ type: 'STAFF_SELECTED', staffId: bypassStaff ? null : soloStaff });
      dispatch({ type: 'STEP_SET', value: 'TIME' });
      const tz = state.bootstrap?.location?.timezone ?? 'UTC';
      const today = new Date().toISOString().slice(0, 10);
      const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
      const { dates } = await api.availableDates(cart.id, today, end, tz);
      dispatch({ type: 'DATES_LOADED', dates });
    } else {
      dispatch({ type: 'STEP_SET', value: 'STAFF' });
    }
  }

  // Phase 7 — D55b. Continue from the Enhance step. Add-ons get pushed onto
  // the cart as additional lines so Square's availability search treats them
  // as a multi-segment booking on the same provider. Modifier picks remain
  // client-side; they don't ride on Square — the salon collects the
  // difference at the appointment.
  async function enhanceContinue() {
    if (!state.cart) {
      failNoCart();
      return;
    }
    // Defense-in-depth gate. The RunningBar's `disabled` prop should
    // already block this click when the required-add-on gate isn't
    // satisfied, but a keyboard shortcut or fast double-tap could
    // bypass the button-disabled state. Re-check at the handler.
    const baseId = state.selectedServiceId;
    const upsells = baseId ? state.bootstrap?.upsellsByService?.[baseId] : null;
    if (
      upsells &&
      !isRequiredAddOnGateSatisfied({
        addOns: upsells.addOns,
        selectedAddOnIds: state.selectedAddOnIds,
      })
    ) {
      // No-op — the Enhance step's inline banner already explains why.
      return;
    }
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      let cart = state.cart;
      const baseServiceId = state.selectedServiceId;
      const existingAddOnIds = new Set(
        cart.lines.filter((l) => l.serviceId !== baseServiceId).map((l) => l.serviceId),
      );
      // Add any newly-selected add-ons that aren't already on the cart.
      for (const addOnId of state.selectedAddOnIds) {
        if (existingAddOnIds.has(addOnId)) continue;
        cart = await api.addService(cart.id, { serviceId: addOnId });
      }
      // Remove any previously-added add-ons that the user just unchecked.
      const stillSelected = new Set(state.selectedAddOnIds);
      for (const line of cart.lines) {
        if (line.serviceId === baseServiceId) continue;
        if (!stillSelected.has(line.serviceId)) {
          cart = await api.removeService(cart.id, line.lineId);
        }
      }
      dispatch({ type: 'CART_SET', value: cart });
      const upsellPayload = {
        addons: state.selectedAddOnIds.length,
        modifiers: state.selectedModifierIds.length,
        metadata: attribution,
      };
      track('upsell_continue', upsellPayload);
      pushDataLayer('upsell_continue', upsellPayload);
      await advancePastEnhance(cart);
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      dispatch({
        type: 'SET_ERROR',
        value: e instanceof Error ? e.message : String(e),
      });
    }
  }

  /**
   * Restore a cart to a snapshot after a failed teardown+rebuild.
   *
   * Swapping staff (or the base service) removes every line before re-adding
   * it. If a re-add fails midway — e.g. the provider rejects the chosen
   * stylist — the customer is left holding an EMPTY cart and every follow-up
   * call fails, which is how an Adelaide "Long Hair Cut" booking died on
   * 2026-08-09. Unwinding to the snapshot turns that into a recoverable
   * error message with the cart intact.
   *
   * Best-effort by design: each step is individually guarded so a failure
   * while restoring can't mask the ORIGINAL error the caller is about to
   * surface. Clears any partial state first so the end state matches the
   * snapshot exactly rather than doubling up half-rebuilt lines.
   */
  async function restoreCartLines(
    cart: Cart,
    snapshot: ReadonlyArray<{ serviceId: string; staffId?: string }>,
  ): Promise<Cart> {
    let restored = cart;
    for (const line of [...restored.lines]) {
      try {
        restored = await api.removeService(restored.id, line.lineId);
      } catch {
        /* keep unwinding — the caller's error is the one that matters */
      }
    }
    for (const line of snapshot) {
      const hiddenSvc = state.bootstrap?.services.find((s) => s.id === line.serviceId && s.hidden);
      try {
        restored = await api.addService(restored.id, {
          serviceId: line.serviceId,
          ...(line.staffId ? { staffId: line.staffId } : {}),
          ...(hiddenSvc?.unlistedToken ? { unlistedToken: hiddenSvc.unlistedToken } : {}),
        });
      } catch {
        /* best-effort */
      }
    }
    return restored;
  }

  async function selectStaff(staffId: string | null) {
    if (!state.cart) {
      failNoCart();
      return;
    }
    if (!state.selectedServiceId) return;
    dispatch({ type: 'STAFF_SELECTED', staffId });
    const staff = staffId ? (state.bootstrap?.staff.find((s) => s.id === staffId) ?? null) : null;
    const stylistName = staff?.displayName ?? null;
    const staffPayload = { staff_id: staffId, metadata: attribution };
    track('staff_selected', staffPayload);
    pushDataLayer('staff_selected', staffPayload);
    // Funnel-event spec name (stylist_selected) fires alongside the
    // legacy staff_selected. Carries the display name so GA4 dimensions
    // read a human-readable label without a server-side join.
    const stylistPayload = {
      staff_id: staffId,
      stylist: stylistName,
      metadata: attribution,
    };
    track('stylist_selected', stylistPayload);
    pushDataLayer('stylist_selected', stylistPayload);
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      // Phase 7 — multi-line carts (D55b add-ons). Snapshot the current
      // service IDs in line order, then teardown + re-add each one with
      // the chosen staff. Without this, picking a staff member would drop
      // any add-ons the user picked on the Enhance step.
      let cart = state.cart;
      // Skip the teardown+readd when every line already matches the
      // chosen staff (or both are "any" / null). Picking the staff a
      // second time, or hitting Continue when the line was already
      // built with that staff (e.g. on Back navigation), used to cost
      // 2N round-trips before the calendar could load.
      const targetStaff = staffId ?? undefined;
      const allMatch = cart.lines.every((l) => (l.staffId ?? undefined) === targetStaff);
      if (!allMatch) {
        const lineServiceIds = cart.lines.map((l) => l.serviceId);
        // Snapshot BEFORE teardown so a failed re-add can be unwound.
        const snapshot = cart.lines.map((l) => ({
          serviceId: l.serviceId,
          ...(l.staffId ? { staffId: l.staffId } : {}),
        }));
        try {
          for (const existing of cart.lines) {
            cart = await api.removeService(cart.id, existing.lineId);
          }
          for (const sid of lineServiceIds) {
            // Preserve the unlisted entitlement on re-add: a deep-linked
            // hidden service that reached this step via its token would
            // otherwise fail the provider's add guard during staff
            // reassignment.
            const hiddenSvc = state.bootstrap?.services.find((s) => s.id === sid && s.hidden);
            cart = await api.addService(cart.id, {
              serviceId: sid,
              staffId: targetStaff,
              ...(hiddenSvc?.unlistedToken ? { unlistedToken: hiddenSvc.unlistedToken } : {}),
            });
          }
        } catch (swapErr) {
          cart = await restoreCartLines(cart, snapshot);
          dispatch({ type: 'CART_SET', value: cart });
          throw swapErr;
        }
        dispatch({ type: 'CART_SET', value: cart });
      }
      dispatch({ type: 'STEP_SET', value: 'TIME' });

      // Fetch available dates: today through 60 days out
      const tz = state.bootstrap?.location?.timezone ?? 'UTC';
      const today = new Date().toISOString().slice(0, 10);
      const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
      const { dates } = await api.availableDates(cart.id, today, end, tz);
      dispatch({ type: 'DATES_LOADED', dates });
    } catch (e) {
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  async function selectDate(date: string) {
    if (!state.cart) {
      failNoCart();
      return;
    }
    dispatch({ type: 'DATE_SELECTED', date });
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const tz = state.bootstrap?.location?.timezone ?? 'UTC';
      const { slots } = await api.availableTimes(state.cart.id, date, tz);
      dispatch({ type: 'SLOTS_LOADED', slots });
    } catch (e) {
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  // Phase 7 — CRO "Next available" auto-pin. As soon as dates resolve,
  // eagerly fetch slots for the soonest day so the Time step lands with a
  // ready-to-tap hero — one fewer click between landing and booking.
  // Skipped when the user has already picked a different date (Back nav).
  useEffect(() => {
    if (state.step !== 'TIME') return;
    if (state.selectedDate) return;
    if (state.availableDates.length === 0) return;
    void selectDate(state.availableDates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.availableDates, state.selectedDate]);

  // Phase 7 Commit J. Tap a slot to RESERVE (locks the hold + starts
  // the countdown), but stay on Time. The RunningBar shows the held
  // slot + remaining hold timer; tapping Continue advances to Contact.
  // Lets the customer see the commitment land before moving forward —
  // anchors urgency on the same screen as the choice.
  async function selectSlot(slot: (typeof state.availableSlots)[number]) {
    if (!state.cart) {
      failNoCart();
      return;
    }
    dispatch({ type: 'SLOT_SELECTED', slot });
    const timePayload = { start_at: slot.startAt, metadata: attribution };
    track('time_selected', timePayload);
    pushDataLayer('time_selected', timePayload);
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const cart = await api.reserveSlot(state.cart.id, slot.slotId);
      dispatch({ type: 'CART_SET', value: cart });
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  function commitTime() {
    if (!state.selectedSlot) return;
    // PHD Studio variant — contact already happened before TIME. If
    // deposits are enabled, go to PAYMENT. If the dashboard toggle
    // flipped depositsEnabled off (or the policy resolves to $0), skip
    // PAYMENT and confirm the booking immediately.
    //
    // Source of truth is state.bootstrap.deposit (the tenant-level
    // policy returned at bootstrap time, always populated). The earlier
    // implementation read state.deposit (the per-cart computed amount,
    // populated by getDeposit during submitContact) which is null until
    // Contact submits AND clears on reload — leading to a reload
    // scenario where the customer lands on TIME with state.deposit=null
    // and commitTime falsely concluded "no deposit" → confirmBooking
    // fired without payment → server-side checkout 400 because the
    // Payment step was skipped.
    const phdStudio = state.bootstrap?.branding?.customViewBundle === 'phd-studio';
    if (phdStudio && state.cart?.client?.givenName) {
      const dep = state.bootstrap?.deposit;
      // PHD-studio path only — that variant doesn't use $0 card-on-file, so the
      // bootstrap deposit (no cardOnFile field) is fine here.
      // Bootstrap's tenant-level policy, deliberately — see the note
      // above on why the per-cart quote is not safe to read here. The
      // already-paid guard is the one thing taken from the quote, and
      // only when it is present: without it this variant re-opens the
      // payment step for a cart that has already been paid for, which
      // is the double charge the shared rule exists to prevent.
      const hasDeposit =
        !!dep?.enabled && (dep.amountCents ?? 0) > 0 && state.deposit?.alreadyPaid !== true;
      if (hasDeposit) {
        dispatch({ type: 'STEP_SET', value: 'PAYMENT' });
      } else {
        void confirmBooking(state.cart);
      }
      return;
    }
    dispatch({ type: 'STEP_SET', value: 'CONTACT' });
  }

  async function submitContact(info: {
    givenName: string;
    familyName?: string;
    email?: string;
    phone: string;
  }) {
    if (!state.cart) {
      failNoCart();
      return;
    }
    identify({ email: info.email, phone: info.phone, name: info.givenName });
    // Funnel-event spec name (booking_submitted). Fires at the moment
    // of intent — before the API round-trip — so a setClient failure
    // doesn't disappear the signal. The user_data block lands on the
    // booking_confirmed event downstream; submit-time captures intent
    // without the booking id (Square hasn't issued one yet).
    const submitPayload = { metadata: attribution };
    track('booking_submitted', submitPayload);
    pushDataLayer('booking_submitted', submitPayload);
    // Public emitter event uses `contact_submitted` to disambiguate from
    // `booking_completed` — internal track()/pushDataLayer() keep the
    // legacy `booking_submitted` name so existing GTM/GA4 wiring is
    // untouched.
    emit({ type: 'contact_submitted', sessionId: sid });
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      // M9 — getDeposit only reads the cart by id, which we already have
      // in state.cart.id; firing the two requests in parallel saves one
      // round-trip on the Contact→Payment/Confirm transition. Dispatch
      // ordering preserved (CART_SET → DEPOSIT_LOADED → STEP_SET) so the
      // step transition still sees the freshly-set cart in state.
      const [cart, deposit] = await Promise.all([
        api.setClient(state.cart.id, info),
        api.getDeposit(state.cart.id),
      ]);
      dispatch({ type: 'CART_SET', value: cart });
      dispatch({ type: 'DEPOSIT_LOADED', deposit });
      // Route to PAYMENT when there's a deposit to charge OR a $0
      // card-on-file to collect — and NOT when this cart has already
      // been paid for, which is how a customer who came back round the
      // loop ends up paying twice. See lib/payment-step.
      const hasDeposit = paymentStepNeeded(deposit);

      // PHD Studio variant — flow is Contact → Time → Payment.
      // When contact submits and no slot is reserved yet, fan out to
      // TIME (loading dates the same way the post-service handler does
      // for tenants with bypassStaffSelection). The PAYMENT transition
      // happens later, from commitTime.
      const phdStudio = state.bootstrap?.branding?.customViewBundle === 'phd-studio';
      const hasSlot = !!cart.lines[0]?.startAt;
      if (phdStudio && !hasSlot) {
        dispatch({ type: 'STEP_SET', value: 'TIME' });
        // Skip the dates fetch if the bootstrap-side PHD prefetch
        // already populated availableDates during the time the user
        // spent on Contact. Common case — saves a roundtrip and a
        // re-render.
        if (state.availableDates.length === 0) {
          const tz = state.bootstrap?.location?.timezone ?? 'UTC';
          const today = new Date().toISOString().slice(0, 10);
          const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
          try {
            const { dates } = await api.availableDates(cart.id, today, end, tz);
            dispatch({ type: 'DATES_LOADED', dates });
          } catch {
            // Date load failure is recoverable — the Time step renders
            // an empty state and the user can retry by tapping a date.
          }
        }
        dispatch({ type: 'SET_LOADING', value: false });
        return;
      }

      // Skip-confirm only fires on the no-deposit path here. With a
      // deposit, the customer still goes to PAYMENT — the skip applies
      // on the OTHER side of that step (in attachPayment).
      if (!hasDeposit && (state.bootstrap?.behavior?.skipConfirmStep ?? false)) {
        dispatch({ type: 'SET_LOADING', value: false });
        await confirmBooking(cart);
        return;
      }
      const nextStep = hasDeposit ? 'PAYMENT' : 'CONFIRM';
      dispatch({ type: 'STEP_SET', value: nextStep });
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  /**
   * P4 — the customer switched between paying the deposit and paying in
   * full. Sends only the CHOICE; the amount comes back derived from the
   * cart (D68). Re-reads the deposit afterwards so the Pay button, the
   * per-line breakdown and the policy copy all move together — the promo
   * path below learned the same lesson.
   */
  async function setPaymentMode(payInFull: boolean): Promise<void> {
    if (!state.cart) return;
    const cartId = state.cart.id;
    // This is also what disables BOTH mode buttons and the Pay button
    // (PaymentStep reads it as `submitting`). Without it two quick taps
    // fire overlapping POST/GET pairs with no sequencing: the later GET
    // can resolve first, landing the OLDER quote while the server holds
    // the newer mode — the customer then reads one amount on the button
    // and is charged the other. Same reason the Pay button must not be
    // pressable while the replacement quote is still in flight.
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      await api.setPaymentMode(cartId, payInFull);
      const deposit = await api.getDeposit(cartId);
      dispatch({ type: 'DEPOSIT_LOADED', deposit });
      setQuoteStale(false);
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      // The dangerous half is when the MODE change landed and the
      // replacement quote did not: the server then holds one amount and
      // the screen shows another, and SET_ERROR clearing `loading` would
      // let the customer pay against the stale figure.
      //
      // Re-read the quote once. The deposit route derives from the
      // server's own cart.payInFull, so a successful resync makes screen
      // and server agree again whichever way the mode landed.
      try {
        const resynced = await api.getDeposit(cartId);
        dispatch({ type: 'DEPOSIT_LOADED', deposit: resynced });
        setQuoteStale(false);
      } catch {
        // Still can't establish what the server holds — block payment
        // rather than charge an amount we cannot vouch for.
        setQuoteStale(true);
      }
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  /**
   * Promo code applied / removed.
   *
   * Refetches the cart AND the deposit together. A code changes the
   * subtotal, and the deposit is a share of that subtotal — showing the
   * new total against the old deposit would be the one genuinely
   * misleading state on this screen, since the deposit is what the card
   * is actually charged.
   */
  async function applyPromo(code: string): Promise<{ ok: boolean; message?: string }> {
    if (!state.cart) return { ok: false, message: 'Your booking is no longer active.' };
    const cartId = state.cart.id;
    const res = await api.applyDiscount(cartId, code);
    if (!res.ok) return { ok: false, message: res.message };
    const [cart, deposit] = await Promise.all([api.getCart(cartId), api.getDeposit(cartId)]);
    dispatch({ type: 'CART_SET', value: cart });
    dispatch({ type: 'DEPOSIT_LOADED', deposit });
    setPromoLabel(res.code);
    return { ok: true };
  }

  async function removePromo(): Promise<void> {
    if (!state.cart) return;
    const cartId = state.cart.id;
    await api.removeDiscount(cartId);
    const [cart, deposit] = await Promise.all([api.getCart(cartId), api.getDeposit(cartId)]);
    dispatch({ type: 'CART_SET', value: cart });
    dispatch({ type: 'DEPOSIT_LOADED', deposit });
    setPromoLabel(null);
  }

  async function attachPayment(
    sourceId: string,
    _verificationToken?: string,
    cardMeta?: {
      brand?: string;
      last4?: string;
      expMonth?: number;
      expYear?: number;
    },
    policyAccepted?: boolean,
    policyTextSeen?: string,
  ) {
    if (!state.cart) {
      failNoCart();
      return;
    }
    // Capture before the await so the non-null narrowing survives it (TS
    // resets property narrowing across `await`). Also the fallback cart for
    // the sb-vault path below.
    const currentCart = state.cart;
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const result = await api.attachPayment(currentCart.id, sourceId, {
        cardMeta,
        policyAccepted,
        policyTextSeen,
      });
      const paymentPayload = {
        // D78 — an amount can identify a treatment as surely as its name.
        value:
          state.deposit &&
          !isSensitive(state.bootstrap?.services.find((s) => s.id === state.selectedServiceId)) &&
          !cartIsSensitive(currentCart, state.bootstrap?.services)
            ? state.deposit.amountCents / 100
            : undefined,
        currency: state.deposit?.currency ?? 'USD',
        // D71 — in card-on-file mode `value` is the deposit the SALON will
        // charge later, not money we collected. Tagged so reports can keep
        // the two apart.
        mode: state.deposit?.cardOnFile ? 'card-on-file' : 'deposit',
        metadata: attribution,
      };
      track('payment_attached', paymentPayload);
      dispatch({
        type: 'CARD_ATTACHED',
        card: cardMeta ? { brand: cardMeta.brand, last4: cardMeta.last4 } : null,
      });
      pushDataLayer('payment_attached', paymentPayload);
      // The sb-vault card-on-file path returns { paymentAttached: true } — not
      // a cart. Storing a card never mutates cart contents, so the cart we
      // already hold is exactly what CONFIRM needs. Square/Stripe/Boulevard
      // return the refreshed cart (it carries an `id`). Only replace
      // state.cart when a real cart came back; otherwise keep ours so CONFIRM
      // (which reads cart.lines/subtotal/client) renders instead of throwing
      // on a line-less object — the "card saved, nothing happens" freeze.
      const cart = 'id' in result ? result : currentCart;
      if ('id' in result) {
        dispatch({ type: 'CART_SET', value: result });
      }
      if (state.bootstrap?.behavior?.skipConfirmStep ?? false) {
        dispatch({ type: 'SET_LOADING', value: false });
        await confirmBooking(cart);
        return;
      }
      dispatch({ type: 'STEP_SET', value: 'CONFIRM' });
      dispatch({ type: 'SET_LOADING', value: false });
    } catch (e) {
      // D71 — the server says this cart owes no card (every line's stylist is
      // exempt, or the mode was switched off mid-hold). The card was NOT
      // stored. Re-quote and, if the fresh quote agrees, book without one.
      if (e instanceof WidgetApiError && isCardStateMismatch(e.code)) {
        try {
          const fresh = await api.getDeposit(currentCart.id);
          dispatch({ type: 'DEPOSIT_LOADED', deposit: fresh });
          const stillNeedsCard =
            fresh.enabled && (fresh.amountCents > 0 || fresh.cardOnFile === true);
          if (!stillNeedsCard) {
            await confirmBooking(currentCart, { cardStateRetried: true });
            return;
          }
        } catch {
          /* fall through to the error screen */
        }
      }
      dispatch({ type: 'SET_ERROR', value: e instanceof Error ? e.message : String(e) });
    }
  }

  // `cartOverride` is the just-returned cart from setClient/attachPayment
  // when we're chaining straight into confirmBooking() on the skip-confirm
  // path — without it, the closure still holds the pre-dispatch cart and
  // the analytics payload (user_data.*) reads stale client info.
  //
  // Defensive type-guard: a previous version of this function took no args
  // and was wired as `onConfirm={confirmBooking}`. Preact passes the click
  // MouseEvent as the first arg, which then masqueraded as a cart and made
  // every confirm-step booking call checkout with cartId=undefined. Reject
  // anything that doesn't look like a cart so a future caller can't repeat
  // the mistake.
  async function confirmBooking(
    cartOverride?: NonNullable<typeof state.cart>,
    opts: {
      cardStateRetried?: boolean;
      /** Back from a payment redirect: one shared checkout per merchant. */
      resumed?: boolean;
    } = {},
  ) {
    const validOverride =
      cartOverride && typeof (cartOverride as { id?: unknown }).id === 'string'
        ? cartOverride
        : null;
    const cart = validOverride ?? state.cart;
    if (!cart || !state.bootstrap) return;
    dispatch({ type: 'SET_LOADING', value: true });
    // Hoisted so the failure path also knows whether it is the instance
    // that reports.
    let shared: { result: Promise<Booking>; leader: boolean } | null = null;
    try {
      const run = () => api.checkout(cart.id, idempotencyKey, serverAttribution());
      shared = opts.resumed ? checkoutResumedOnce(merchant, run) : null;
      const booking = await (shared ? shared.result : run());
      // Another instance on this page ran the resumed checkout and has
      // already reported it; this one only shows Done.
      // It still announces its OWN completion: the host page listens per
      // session.
      if (shared && !shared.leader) {
        emit({ type: 'booking_completed', booking, sessionId: sid });
        dispatch({ type: 'BOOKING_CONFIRMED', booking });
        return;
      }
      // Phase 1c — enriched booking_confirmed payload. event_id pins the
      // dedup contract with the server-side webhook fire (both use the
      // booking id). transaction_id is the analytics field GA4 reads.
      // user_data carries raw email/phone/name for FireTrack to hash
      // server-side (D25 PII pattern); GTM's GA4 Enhanced Conversions
      // tag also reads from this shape.
      const service = state.bootstrap.services.find((s) => s.id === state.selectedServiceId);
      const staff = state.selectedStaffId
        ? (state.bootstrap.staff.find((s) => s.id === state.selectedStaffId) ?? null)
        : null;
      const client = cart.client;
      // D78 — a health-revealing service is reported generically: no
      // name, category or value (its price would name it too). The
      // conversion value still reaches FireTrack from the server.
      const sensitive = isSensitive(service) || cartIsSensitive(cart, state.bootstrap.services);
      const bookingPayload = {
        event_id: booking.id,
        transaction_id: booking.id,
        value: sensitive ? undefined : service ? service.price.amountCents / 100 : undefined,
        currency: service?.price.currency ?? 'USD',
        service_type: sensitive ? null : (service?.categoryName ?? null),
        service_name: sensitive ? SENSITIVE_SERVICE_LABEL : (service?.name ?? null),
        stylist: staff?.displayName ?? null,
        stylist_id: state.selectedStaffId ?? null,
        appointment_type: null as string | null,
        user_data: {
          email_address: client?.email ?? null,
          phone_number: client?.phone ?? null,
          first_name: client?.givenName ?? null,
          last_name: client?.familyName ?? null,
        },
        metadata: { booking_id: booking.id, ...attribution },
      };
      // FireTrack fire uses the per-service event-name override when set
      // (e.g. consultation_booked) so it matches the SERVER dispatcher's
      // event_type and FireTrack's event_id dedup still collapses the two
      // fires. GTM/GA4 keep the stable dataLayer name regardless.
      // A sensitive booking fires the generic name, as the server does, so
      // a custom event name cannot name the condition and dedup stays paired.
      track(
        (sensitive ? null : service?.firetrackEventName) || 'booking_confirmed',
        bookingPayload,
      );
      pushDataLayer('booking_confirmed', bookingPayload);
      emit({ type: 'booking_completed', booking, sessionId: sid });
      dispatch({ type: 'BOOKING_CONFIRMED', booking });
    } catch (e) {
      // EVERY checkout failure is reported here, before the branching
      // below decides how to recover. This is the only signal for the
      // recoverable ones: the server deliberately keeps CART_EXPIRED out
      // of Sentry (checkout/route.ts:149) because a lapsed 30-min hold
      // is expected user-timing behaviour, not a contract bug. That was
      // fine while Boulevard's lapsed holds mapped to something else —
      // once they became CART_EXPIRED too, those failures stopped
      // producing a signal anywhere at all. Without this line the fix
      // and the blindfold ship together.
      const apiErr = e instanceof WidgetApiError ? e : null;
      const rawMessage = e instanceof Error ? e.message : String(e);
      // One decision, used twice — see checkoutRecoveryMessage. Deriving
      // the report from the same call that drives the behaviour is what
      // stops "recovered: true" from outliving the branch that made it
      // true.
      const recovery = checkoutRecoveryMessage(apiErr?.code, rawMessage);
      // D71 — the server's card requirement disagrees with the quote we hold
      // (PAYMENT_REQUIRED: a card is needed and none was saved). Recoverable
      // once: fetch a fresh quote and route by it — to the card step when it
      // says card, straight back to checkout when it says none. A second
      // disagreement dead-ends like any other failure rather than looping.
      const cardStateMismatch =
        !!apiErr && isCardStateMismatch(apiErr.code) && !opts.cardStateRetried;
      // P5.3 — the deferred payment will never complete (declined, or
      // abandoned at Klarna / Affirm / Afterpay). Back to the payment step,
      // with the reason, so they can try again or choose another method.
      // Staying on Confirm dead-ended them (Be You, 2026-10-12): its
      // button books but cannot take a payment.
      const paymentFailed = apiErr?.code === 'PAYMENT_FAILED';

      if (!shared || shared.leader) track('checkout_failed', {
        merchant,
        cart_id: cart.id,
        code: apiErr?.code ?? 'UNKNOWN',
        status: apiErr?.status ?? 0,
        // Deliberately NO message. Provider errors can echo back
        // submitted values, and truncating is not redacting — this event
        // goes to the analytics pipeline, which is the wrong place to
        // risk customer data. The code and status are what make the
        // failure countable; the full text is still captured server-side
        // in Sentry and the logs, where redaction applies.
        // Whether the customer gets a way forward or an error screen.
        recovered: recovery !== null || cardStateMismatch || paymentFailed,
      });

      if (paymentFailed) {
        let quote = state.deposit;
        try {
          quote = await api.getDeposit(cart.id);
          dispatch({ type: 'DEPOSIT_LOADED', deposit: quote });
        } catch {
          // Keep the quote we hold; it only delays the price.
        }
        // Both payment components render from the quote. With none at
        // all (a resumed cart whose quote never loaded), the payment step
        // would be blank — Contact's Continue re-quotes and routes back
        // to it.
        dispatch({ type: 'STEP_SET', value: quote ? 'PAYMENT' : 'CONTACT' });
        // After STEP_SET, which clears `error`.
        dispatch({ type: 'SET_ERROR', value: rawMessage });
        dispatch({ type: 'SET_LOADING', value: false });
        return;
      }

      if (cardStateMismatch) {
        try {
          const fresh = await api.getDeposit(cart.id);
          dispatch({ type: 'DEPOSIT_LOADED', deposit: fresh });
          // D71's own rule, unchanged, AND the already-paid guard.
          // Deliberately a conjunction rather than a swap: this branch
          // never consulted `allowPayInFull`, and folding that clause in
          // here would send a prepay-enabled tenant to the card step on
          // a $0 quote where it used to retry the booking.
          const needsCard =
            fresh.enabled &&
            (fresh.amountCents > 0 || fresh.cardOnFile === true) &&
            paymentStepNeeded(fresh);
          if (needsCard) {
            dispatch({ type: 'STEP_SET', value: 'PAYMENT' });
            dispatch({ type: 'SET_LOADING', value: false });
            return;
          }
          await confirmBooking(cart, { cardStateRetried: true });
          return;
        } catch {
          /* fall through to the error screen */
        }
      }
      if (recovery !== null) {
        await recoverToTimeStep(cart.id, recovery);
        return;
      }
      if (opts.resumed) {
        // Paid, but the booking call itself failed. Confirm is the one
        // screen whose button retries the booking WITHOUT taking another
        // payment; the payment step's Pay would only be refused.
        dispatch({ type: 'STEP_SET', value: 'CONFIRM' });
      }
      dispatch({ type: 'SET_ERROR', value: rawMessage });
    }
  }

  /**
   * Recovery path for a slot that's gone (expired hold or a provider-side
   * conflict at booking time): re-sync the cart, return the customer to
   * the Time step with a fresh availability window, and explain what
   * happened. Their service and contact details stay on the cart —
   * they're one tap from rebooking.
   */
  // Phase 6 — start buying a Special Service. Nameless analytics (D78).
  function startPurchase(product: ShopProduct, label: string) {
    track('special_service_selected', { metadata: attribution });
    pushDataLayer('special_service_selected', { metadata: attribution });
    setPurchase({ product, label });
  }

  // Phase 6 — `?product=<id>` opens that Special Service's purchase. Also
  // the only way to reach a hidden (test) product.
  useEffect(() => {
    if (productLinkRef.current || !state.bootstrap?.shop || !dl?.productId) return;
    const p = state.bootstrap.shop.products.find((x) => x.id === dl.productId);
    // Only a match spends the link: a cached bootstrap without the product
    // gets another chance when the background refresh lands.
    if (!p) return;
    productLinkRef.current = true;
    startPurchase(p, p.groupName ? `${p.groupName} · ${p.optionLabel ?? ''}` : p.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bootstrap]);

  // P5.2 D — finish a resumed cart's booking once the widget is ready.
  // Runs once: the ref is cleared before the call so a re-render cannot
  // check out twice.
  useEffect(() => {
    const cart = resumeCheckoutRef.current;
    if (!cart || !state.bootstrap || state.step !== 'PAYMENT') return;
    resumeCheckoutRef.current = null;
    void confirmBooking(cart, { resumed: true }).finally(() => setFinishingPayment(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bootstrap, state.step]);

  async function recoverToTimeStep(cartId: string, message: string) {
    let alreadyPaid: boolean | undefined;
    try {
      // The QUOTE as well as the cart. Everything downstream that asks
      // "has this cart been paid for?" reads `state.deposit`, and
      // landing here without refreshing it left that answer frozen at
      // its pre-payment value — which made the already-paid guard on
      // the Contact→next routing inert in exactly the loop it was
      // written for (pay, sniped slot, back to Time, Continue).
      const [fresh, quote] = await Promise.all([api.getCart(cartId), api.getDeposit(cartId)]);
      dispatch({ type: 'CART_SET', value: fresh });
      dispatch({ type: 'DEPOSIT_LOADED', deposit: quote });
      alreadyPaid = (quote as { alreadyPaid?: boolean } | null)?.alreadyPaid;
    } catch {
      // Re-sync is best-effort; the Time step re-reserves onto the same
      // cart id regardless.
    }
    // The held slot is gone — clear the stale date/slot selection so the
    // running bar's Continue disables and the Time step re-pins the first
    // available date. Order matters: STEP_SET clears state.error, so the
    // banner message must be dispatched after it.
    dispatch({ type: 'TIME_SELECTION_CLEARED' });
    dispatch({ type: 'STEP_SET', value: 'TIME' });
    dispatch({ type: 'SET_ERROR', value: recoveryBanner(message, alreadyPaid) });
    try {
      const tz = state.bootstrap?.location?.timezone ?? 'UTC';
      const today = new Date().toISOString().slice(0, 10);
      const end = new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10);
      const { dates } = await api.availableDates(cartId, today, end, tz);
      dispatch({ type: 'DATES_LOADED', dates });
    } catch {
      // Date load failure is recoverable — the Time step renders an
      // empty state and the user can retry by tapping a date.
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (state.error && !state.bootstrap) {
    return (
      <div class="sbw-fatal">
        <p>Sorry — we couldn't load the booking widget.</p>
        <p class="sbw-error">{state.error}</p>
      </div>
    );
  }

  // Cart init failed. Bootstrap succeeded, so without this the catalogue would
  // render as normal and every button would be silently inert for the rest of
  // the session. Checked BEFORE the step render, and driven by its own flag
  // rather than `error` — STEP_SET deliberately clears `error`, which would
  // have thrown away the only explanation the customer ever got.
  if (state.cartInitFailed) {
    return (
      <div class="sbw-fatal">
        <p>Sorry — we couldn't start your booking.</p>
        <p class="sbw-error">{state.error}</p>
        <button
          type="button"
          class="sbw-btn sbw-btn--primary"
          onClick={() => {
            dispatch({ type: 'CART_INIT_RETRY' });
            setRetryNonce((n) => n + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!state.bootstrap) {
    return <div class="sbw-loading">Loading…</div>;
  }

  const b = state.bootstrap;
  const locationName = b.location?.name ?? b.business.name;
  // The chrome's top-left slot is a wordmark, not a location label. When a
  // tenant has no logoUrl it renders this as text, so it must be the business
  // ("Odessa Hair Co."), never the location ("Spanish Fork"). Every tenant
  // before Odessa set a logoUrl, which hid the difference.
  const businessName = b.business.name || locationName;
  const timezone = b.location?.timezone ?? 'UTC';

  // Phase 7 Commit J — apply per-widget brand color as the --sbw-brand
  // CSS custom property on the shadow root. The tone preset class
  // already sets a default brand for the family; this override (when
  // the operator has set a brand color) wins.
  //
  // Brand-ink picker: when an operator picks a light brand (pastel,
  // yellow, near-white), white text on top becomes unreadable. Compute
  // perceived luminance of the brand and flip --sbw-brand-ink to near-
  // black when the brand is light. BT.601 luminance is good enough for
  // UI buttons; threshold 0.6 keeps warm tones (terracotta, orange,
  // brick) on white ink and only flips genuinely-light brands.
  const brandingStyle: Record<string, string> = {};
  if (b.branding?.brandColorHex) {
    brandingStyle['--sbw-brand'] = b.branding.brandColorHex;
    brandingStyle['--sbw-brand-ink'] = pickInkForBg(b.branding.brandColorHex);
    brandingStyle['--sbw-brand-fg'] = pickBrandFg(b.branding.brandColorHex);
  }
  if (b.branding?.accentColorHex) {
    // The new token system uses --sbw-brand for everything; accent isn't
    // a separate token in the design. Still surface for legacy components.
    brandingStyle['--sbw-accent'] = b.branding.accentColorHex;
  }
  // Phase 7 Commit I — tone preset class. Swaps the design-token bundle
  // (radius, shadows, surface bg, font) without changing component CSS.
  // Defaults to 'modern' when the bootstrap omits it.
  const tonePreset = b.branding?.tonePreset ?? 'modern';
  // Per-tenant view-component bundle. When set (e.g. 'be-you' for the
  // Be You Esthetic Suites med-spa), the root gets `sbw-variant-{value}`
  // and bespoke step components render. All variant CSS lives ONLY under
  // .sbw-variant-* selectors — H22-safe for every other tenant.
  const customViewBundle = b.branding?.customViewBundle ?? null;
  /**
   * ONE decision, so the two PAYMENT branches are true complements.
   *
   * Written as three separate conditions, the Element branch could be
   * false (no publishable key) while the Card Element branch was ALSO
   * false (usePaymentElement true) — and the payment step would render
   * nothing at all: no form, no error, no way forward. Deciding once
   * means the customer always gets one of the two.
   *
   * A tenant flagged for the Element but missing a key therefore falls
   * back to the Card Element rather than to a blank screen.
   */
  const paymentElement =
    state.deposit?.usePaymentElement === true &&
    state.deposit.tokenization.kind === 'stripe' &&
    state.deposit.tokenization.publicKey
      ? { publishableKey: state.deposit.tokenization.publicKey }
      : null;
  // Be You's stored logoUrl points at a hotlink-protected WordPress asset
  // (beyouestheticsuites.com → HTTP 403), which renders a broken <img>.
  // Force the canonical, CDN-hosted Be You wordmark for the be-you variant;
  // all other tenants keep their configured logoUrl.
  const resolvedLogoUrl =
    customViewBundle === 'be-you'
      ? 'https://salonboss.media/be-you-esthetics/be-you-logo-horizontal-1.svg'
      : (b.branding?.logoUrl ?? undefined);
  const variantClass = customViewBundle ? ` sbw-variant-${customViewBundle}` : '';
  // Per-tenant root class derived from the merchant slug. Lets CSS opt
  // tenants into one-off treatments (e.g. Good Vibes' pulsing Continue
  // button) without polluting the schema with a flag per tweak. Same
  // H22-safe pattern as variantClass — only the targeted slug matches.
  // Sanitize to [a-z0-9-] in case a future slug ever contains anything
  // exotic; today every slug is already kebab-case.
  const tenantSlugClass = merchant
    ? ` sbw-tenant-${merchant.toLowerCase().replace(/[^a-z0-9-]/g, '-')}`
    : '';
  const rootClass = `sbw-root sbw-tone-${tonePreset}${variantClass}${tenantSlugClass}`;
  // Be You sub-step state. The widget now lands directly on the flat
  // service list — categories are filter tabs at the top, not a separate
  // step. Default tab is 'all'. Only used when customViewBundle === 'be-you'.
  //
  // Initial value honors a deep-link ?category=<id>: lets the merchant
  // surface a hidden member-perk collection (e.g. Skin Club) via a single
  // URL without exposing it in the public catalog. The BeYouView itself
  // gates on whether the category is hidden; here we just seed the tab.
  // P4: set when a payment-mode change left the server and the screen
  // possibly disagreeing about the amount. Blocks payment until a quote
  // we trust has loaded — see setPaymentMode's catch.
  const [quoteStale, setQuoteStale] = useState(false);
  const [beYouActiveTab, setBeYouActiveTab] = useState<string>(dl?.categoryId ?? 'all');
  const [beYouSelectedSvcId, setBeYouSelectedSvcId] = useState<string | null>(null);
  const [beYouExpandedSvcId, setBeYouExpandedSvcId] = useState<string | null>(null);
  // Per-tier price + deposit (cents) of the currently-picked Be You tier.
  // Drives the running bar's CONTINUE button and deposit/total display.
  // Server-side cart.subtotal still pulls from GHL service price (or a
  // WidgetServiceOverride) — these client-side values are display-only
  // until the per-tier overrides are seeded in WidgetServiceOverride.
  const [beYouPriceCents, setBeYouPriceCents] = useState(0);
  const [beYouDepositCents, setBeYouDepositCents] = useState(0);
  // Display label for the running bar — full service name + tier (e.g.
  // "Botox · 2 areas · 40 units" instead of the design slug 'botox').
  const [beYouSelectedLabel, setBeYouSelectedLabel] = useState<string | null>(null);
  // True when the selected Be You service is a free consultation — drives
  // consult-specific copy on the Time step ("Reserve your consultation").
  const [beYouIsConsult, setBeYouIsConsult] = useState(false);

  // Phase 7 — CRO message-match + Commit H per-widget copy. Resolution order:
  //   1. widget.headerCopy — operator picked this widget's curated copy;
  //      wins over an ad-side ?headline= since the widget pairs intentional
  //      visual + copy choices that shouldn't be overridden by a stray param.
  //   2. ?headline= or utm_content — ad-creative match for tracked links.
  //   3. The location name — default tenant hero.
  // 80-char cap is defensive even though the upstream parser already
  // capped at 256 — a hero longer than that wraps badly on mobile.
  const heroCopy = b.headerCopy ?? (dl?.headline ? dl.headline.slice(0, 80) : locationName);

  // Phase 7 Commit J. Compute the full step list this widget will use so
  // the progress bar shows accurate "X% there". Mirror the same logic
  // the legacy Progress component used.
  // Mount the card step when there's a deposit to charge OR when the tenant is
  // in "$0 card-on-file" mode (card required, nothing charged today).
  // Same rule the routing uses, so the progress bar cannot promise a
  // step the customer is never sent to. See lib/payment-step.
  const includePayment = paymentStepNeeded(state.deposit);
  const includeEnhance =
    !!state.selectedServiceId &&
    ((b.upsellsByService?.[state.selectedServiceId]?.addOns.length ?? 0) > 0 ||
      (b.upsellsByService?.[state.selectedServiceId]?.modifiers.length ?? 0) > 0);
  const bypassStaff = b.behavior?.bypassStaffSelection ?? false;
  const skipConfirm = b.behavior?.skipConfirmStep ?? false;
  // M16 — flow array literal would otherwise be a fresh reference every
  // render; the Chrome's totalSteps + flow.indexOf() reads are cheap, but
  // keeping the array stable means downstream memoization (and equality
  // checks) actually short-circuit.
  const flow = useMemo<(typeof state.step)[]>(
    () => [
      'SERVICE',
      ...(includeEnhance ? (['ENHANCE'] as const) : ([] as const)),
      ...(bypassStaff ? ([] as const) : (['STAFF'] as const)),
      'TIME',
      'CONTACT',
      ...(includePayment ? (['PAYMENT'] as const) : ([] as const)),
      ...(skipConfirm ? ([] as const) : (['CONFIRM'] as const)),
    ],
    [includeEnhance, bypassStaff, includePayment, skipConfirm],
  );
  // DONE isn't part of the visible flow, but the chrome's progress bar
  // should still read 100% on the success screen — flow.indexOf('DONE')
  // returns -1, which gets clamped to 0 (i.e. ~23%) without this guard.
  const stepIndex = state.step === 'DONE' ? flow.length : Math.max(0, flow.indexOf(state.step));
  // Hide hero on every step after Service — density wins on the longer
  // forms; hero re-anchors only when the customer might bounce (Service).
  // Also drop the hero when there's no campaign override (heroCopy ===
  // locationName), since the chrome's top-left logo already shows that.
  const hasCampaignHero = !!b.headerCopy || !!(dl?.headline && dl.headline.length > 0);
  // PHD Studio skips the Service step entirely; suppress the campaign
  // hero too so it doesn't flash during the bootstrap-→-CONTACT lap.
  const showHero =
    state.step === 'SERVICE' && !purchase && hasCampaignHero && customViewBundle !== 'phd-studio';
  // Slot-hold strip pinned from Time onward whenever a hold is live.
  // M15 — Date + toLocaleString allocations only when the slot or tz
  // actually changes, not on every state tick.
  const heldSlotLabel = useMemo(() => {
    if (!state.selectedSlot) return null;
    const d = new Date(state.selectedSlot.startAt);
    return d.toLocaleString('en-US', {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  }, [state.selectedSlot?.startAt, timezone]);
  const showHoldStrip =
    !!state.cart?.expiresAt &&
    !!heldSlotLabel &&
    ['TIME', 'CONTACT', 'PAYMENT', 'CONFIRM'].includes(state.step);

  // Selected service / add-on / total — drives the RunningBar summary.
  // H2 — services keyed by id for O(1) lookup; the array find walks the
  // full catalog every render which adds up on big-tenant bootstraps.
  const servicesById = useMemo(() => new Map(b.services.map((s) => [s.id, s])), [b.services]);
  const selectedSvc = useMemo(
    () => (state.selectedServiceId ? (servicesById.get(state.selectedServiceId) ?? null) : null),
    [state.selectedServiceId, servicesById],
  );
  // Consultation services (by name) get consult-flavored Time-step copy —
  // works for any tenant, not just the be-you bundle (Odessa consult flow).
  const isConsultService = /consult/i.test(selectedSvc?.name ?? '');
  // Prefer the cart total once a line is committed AND the cart's base
  // line still matches the highlighted service. Otherwise preview from
  // the highlighted service. The cart is created at bootstrap, so it's
  // almost always non-null but starts with zero lines — falling back
  // only on `!state.cart` would render $0 during the SERVICE step even
  // after the user has highlighted a service. And on back-nav to
  // SERVICE with a different highlight, we'd otherwise display the
  // committed price under the new service's name (mismatch).
  const cartTotal = useMemo(
    () => (state.cart ? state.cart.lines.reduce((s, l) => s + l.price.amountCents, 0) : 0),
    [state.cart],
  );
  const cartMatchesHighlight = useMemo(
    () =>
      !!state.selectedServiceId &&
      !!state.cart?.lines.some((l) => l.serviceId === state.selectedServiceId),
    [state.selectedServiceId, state.cart],
  );
  const lineTotal = cartMatchesHighlight ? cartTotal : (selectedSvc?.price.amountCents ?? 0);
  const lineTotalDollars = Math.round(lineTotal / 100);
  const formatPrice = (cents: number) => `$${Math.round(cents / 100)}`;

  /**
   * Deposit copy derived from the tenant's policy rather than a priced cart,
   * so the Service step can disclose it before the customer has picked
   * anything. TIERED has no single number to quote (the amount depends on the
   * cart subtotal), so it stays generic rather than guessing at a figure.
   */
  const depositPolicyCopy = (d: BootstrapResponse['deposit']): string | null => {
    if (!d || !d.enabled) return null;
    if (d.type === 'PERCENT' && d.percent) {
      return `${d.percent}% deposit holds your appointment`;
    }
    if (d.type === 'FLAT' && d.amountCents) {
      return `${formatPrice(d.amountCents)} deposit holds your appointment`;
    }
    if (d.type === 'TIERED') return 'A deposit holds your appointment';
    return null;
  };

  return (
    <div class={rootClass} style={brandingStyle}>
      <Chrome
        stepIndex={stepIndex}
        totalSteps={flow.length}
        salonName={businessName}
        logoUrl={resolvedLogoUrl}
        hero={heroCopy}
        // Salon name as sub-headline: removed 2026-05-15. Was rendering
        // the business name under a custom hero copy (e.g. "Book your
        // hair appointment" / "KC Beauty Lounge"). The logo already
        // identifies the salon; the second text line just competed with
        // the action-oriented hero on paid-ad LPs. Drop entirely.
        sub={null}
        showHero={showHero}
        trust={b.trust}
        depositHoldCopy={
          // Once a cart exists we know the exact amount, so say it.
          (state.deposit?.enabled ?? false) && (state.deposit?.amountCents ?? 0) > 0
            ? `${formatPrice(state.deposit!.amountCents)} deposit holds your slot`
            : // Before that — i.e. on the Service step, where the customer is
              // actually deciding — there is no cart and no exact figure, so the
              // pill used to be absent entirely and the deposit first appeared
              // at Payment. A charge the customer didn't know about, two steps
              // in, is a classic late-surprise abandon. Bootstrap already
              // carries the policy, so state it up front from that instead.
              // D71: card-on-file tenants with a deposit ladder get their own
              // server-worded notice (bootstrap.deposit is off for them).
              (b.depositNotice ?? depositPolicyCopy(b.deposit))
        }
      />

      {showHoldStrip && heldSlotLabel && (
        <HoldStrip
          expiresAt={state.cart?.expiresAt ?? null}
          whenLabel={heldSlotLabel}
          // Suppressed while a card is being charged, and reported as
          // suppressed so the countdown asks again once it settles —
          // see lib/payment-lock. Navigating here would unmount the
          // Payment step underneath a running confirmPayment: the
          // charge completes anyway, and the customer watches a time
          // picker while it does.
          //
          // Also held off while a request is in flight — above all the
          // booking call `onPaid` makes. The payment lock comes down
          // once the money settles, before that call; navigating then
          // strands a paid customer on a time picker while their booking
          // is being made. `false` keeps the countdown asking, so it
          // fires once the call settles if the hold really is gone.
          onExpired={() =>
            state.loading
              ? false
              : onHoldExpiry(sid, () => {
                  // Same staleness hazard as the CART_EXPIRED recovery: the
                  // expired selection must not leave Continue enabled.
                  dispatch({ type: 'TIME_SELECTION_CLEARED' });
                  dispatch({ type: 'STEP_SET', value: 'TIME' });
                  // AND say why. STEP_SET clears `error`, and when this
                  // fires after a suppressed expiry the customer has just
                  // watched a payment fail — so without a banner they are
                  // teleported to a time picker with no explanation, unsure
                  // whether they were charged. Dispatched after STEP_SET,
                  // which is the ordering recoverToTimeStep already uses.
                  dispatch({
                    type: 'SET_ERROR',
                    value: 'Your held time expired — please pick another time.',
                  });
                })
          }
        />
      )}

      <div class="sbw-stage">
        {/* Server-error banner. Surfaces validation/checkout failures
            at the TOP of the active step so the customer doesn't have
            to scroll past the form to find them. Earlier we lost a
            phone-validation 400 because the error rendered below the
            action bar. STEP_SET clears state.error automatically, so
            navigating between steps wipes stale errors. */}
        {state.error && state.step !== 'DONE' ? (
          <div class="sbw-banner-error" role="alert">
            <span aria-hidden="true" class="sbw-banner-error-icon">
              !
            </span>
            <span class="sbw-banner-error-text">{state.error}</span>
            <button
              type="button"
              class="sbw-banner-error-dismiss"
              onClick={() => dispatch({ type: 'SET_ERROR', value: null })}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        ) : null}
        {finishingPayment && state.step === 'PAYMENT' && (
          <div class="sbw-preselect-notice" role="status">
            <span>Finishing your booking…</span>
          </div>
        )}
        {preselectNotice && state.step === 'SERVICE' && (
          <div class="sbw-preselect-notice" role="status">
            <span>{preselectNotice}</span>
            <button
              type="button"
              class="sbw-preselect-dismiss"
              aria-label="Dismiss"
              onClick={() => setPreselectNotice(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* PHD Studio variant — auto-skips Service step. While the
            bootstrap effect resolves + auto-adds the single calendar,
            show a minimal cream skeleton instead of the default
            Service step (otherwise the catalog grid flashes for ~1s
            before the variant logic flips state.step to CONTACT). */}
        {state.step === 'SERVICE' && customViewBundle === 'phd-studio' && (
          <div class="sbw-scroll" style={{ minHeight: 280 }} aria-busy="true" />
        )}
        {purchase && b.shop && (
          <PurchaseFlow
            api={api}
            sessionId={sid}
            publishableKey={b.shop.publishableKey}
            product={purchase.product}
            label={purchase.label}
            customViewBundle={customViewBundle}
            prefilled={dl?.prefilledContact}
            attribution={() => serverAttribution()}
            report={(event, orderId) => {
              // D78 — no product name, no value; the server reports the value.
              // The purchase carries the SERVER's event id, so FireTrack's
              // dedup collapses the browser and server fires into one.
              const payload = {
                ...(orderId && event === 'special_service_purchase' ? { event_id: `shop-order-${orderId}` } : {}),
                metadata: attribution,
              };
              track(event, payload);
              pushDataLayer(event, payload);
            }}
            onExit={() => setPurchase(null)}
            onConfirmingChange={setPaymentConfirming}
            turnstileSiteKey={b.shop.turnstileSiteKey}
          />
        )}
        {state.step === 'SERVICE' &&
          !purchase &&
          customViewBundle !== 'phd-studio' &&
          (b.serviceFamilies && b.serviceFamilies.length > 0 ? (
            <>
              <ServiceConfigurator
                families={b.serviceFamilies}
                flatFamilies={b.flatFamilies ?? []}
                selectedServiceId={state.selectedServiceId}
                onSelect={selectService}
                onClear={() => dispatch({ type: 'SERVICE_CLEARED' })}
              />
              <RunningBar
                summary={selectedSvc?.name ?? <span>Pick a style to start</span>}
                sub={
                  selectedSvc
                    ? selectedSvc.durationMinutes > 0
                      ? `${selectedSvc.durationMinutes} min · ${formatPrice(selectedSvc.price.amountCents)}`
                      : formatPrice(selectedSvc.price.amountCents)
                    : `${b.serviceFamilies.length} styles`
                }
                primary="Continue"
                total={selectedSvc ? lineTotalDollars : undefined}
                brand
                disabled={!selectedSvc}
                submitting={state.loading}
                onContinue={commitService}
              />
            </>
          ) : customViewBundle === 'be-you' ? (
            <>
              <BeYouServiceView
                activeTab={beYouActiveTab}
                onChangeTab={(tab) => {
                  setBeYouActiveTab(tab);
                  // Collapse any open row when changing tabs so users
                  // don't carry an expanded out-of-context card across.
                  setBeYouExpandedSvcId(null);
                }}
                selectedId={beYouSelectedSvcId}
                expandedId={beYouExpandedSvcId}
                shop={b.shop?.products}
                onSelect={(designId, ghlId, priceCents, depositCents, label, isConsult, productId) => {
                  setBeYouSelectedProductId(productId ?? null);
                  setBeYouSelectedSvcId(designId);
                  setBeYouPriceCents(priceCents);
                  setBeYouDepositCents(depositCents);
                  setBeYouSelectedLabel(label);
                  setBeYouIsConsult(!!isConsult);
                  // Wire the real GHL calendar id into the booking flow's
                  // selectedServiceId — Staff / Time / Checkout use that.
                  if (ghlId) selectService(ghlId);
                }}
                onExpand={(id) => setBeYouExpandedSvcId(id)}
                initialQuery={dl?.searchQuery ?? null}
              />
              <RunningBar
                summary={
                  beYouSelectedLabel ? (
                    <span>{beYouSelectedLabel}</span>
                  ) : (
                    <span>Pick a service to continue</span>
                  )
                }
                sub={
                  beYouSelectedProductId && beYouPriceCents > 0
                    ? `$${(beYouPriceCents / 100).toFixed(0)} + tax · no appointment needed`
                    : beYouSelectedSvcId && beYouPriceCents > 0
                    ? beYouDepositCents < beYouPriceCents
                      ? `$${(beYouDepositCents / 100).toFixed(0)} deposit · $${(beYouPriceCents / 100).toFixed(0)} total`
                      : `$${(beYouPriceCents / 100).toFixed(0)} due today`
                    : 'Select a service to continue'
                }
                primary="Continue"
                // The CONTINUE button shows the amount the customer's
                // card will be charged NOW — the deposit, not the total.
                total={
                  beYouSelectedSvcId && beYouDepositCents > 0 ? beYouDepositCents / 100 : undefined
                }
                brand
                disabled={!beYouSelectedSvcId}
                submitting={state.loading}
                onContinue={() => {
                  const product = beYouSelectedProductId
                    ? b.shop?.products.find((p) => p.id === beYouSelectedProductId)
                    : undefined;
                  if (product) startPurchase(product, beYouSelectedLabel ?? product.name);
                  else void commitService();
                }}
              />
            </>
          ) : (
            <>
              <ServiceStep
                bootstrap={b}
                selectedId={state.selectedServiceId}
                onSelect={selectService}
                categoryFilter={state.categoryFilter}
                onClearCategory={() => dispatch({ type: 'CATEGORY_FILTER_SET', filter: null })}
              />
              <RunningBar
                summary={selectedSvc?.name ?? <span>Pick a service to start</span>}
                sub={
                  selectedSvc
                    ? selectedSvc.durationMinutes > 0
                      ? `${selectedSvc.durationMinutes} min · ${formatPrice(selectedSvc.price.amountCents)}`
                      : formatPrice(selectedSvc.price.amountCents)
                    : `${b.services.length} services`
                }
                primary="Continue"
                total={selectedSvc ? lineTotalDollars : undefined}
                brand
                disabled={!selectedSvc}
                submitting={state.loading}
                onContinue={commitService}
              />
            </>
          ))}

        {state.step === 'ENHANCE' &&
          state.selectedServiceId &&
          (() => {
            // Required-add-on gate. Continue is disabled until the user
            // picks at least one add-on marked `isRequired` (Meevo's
            // numberOfRequiredAddons ≥ 1). Pre-PR-#22, Continue was always
            // enabled and the customer could skip the design-tier
            // selection on Builder Gel Fill etc., leading to
            // underspecified bookings at the salon. See Enhance.tsx for
            // the inline banner that surfaces the same constraint.
            const enhanceUpsells = b.upsellsByService?.[state.selectedServiceId] ?? {
              addOns: [],
              modifiers: [],
            };
            const enhanceGateBlocked = !isRequiredAddOnGateSatisfied({
              addOns: enhanceUpsells.addOns,
              selectedAddOnIds: state.selectedAddOnIds,
            });
            return (
              <>
                <EnhanceStep
                  upsells={enhanceUpsells}
                  selectedAddOnIds={state.selectedAddOnIds}
                  selectedModifierIds={state.selectedModifierIds}
                  onToggleAddOn={(serviceId, groupName, siblings) =>
                    dispatch({ type: 'ADDON_TOGGLED', serviceId, groupName, siblings })
                  }
                  onToggleModifier={(modifierId, groupName, siblings) =>
                    dispatch({
                      type: 'MODIFIER_TOGGLED',
                      modifierId,
                      groupName,
                      siblings,
                    })
                  }
                  showPrices={b.behavior?.showPrices ?? true}
                />
                <RunningBar
                  summary={selectedSvc?.name ?? 'Your booking'}
                  sub={`${state.selectedAddOnIds.length} add-on${state.selectedAddOnIds.length === 1 ? '' : 's'} · ${state.selectedModifierIds.length} option${state.selectedModifierIds.length === 1 ? '' : 's'}`}
                  primary="Continue"
                  total={lineTotalDollars}
                  brand
                  onBack={() => dispatch({ type: 'STEP_SET', value: 'SERVICE' })}
                  submitting={state.loading}
                  disabled={enhanceGateBlocked}
                  onContinue={enhanceContinue}
                />
              </>
            );
          })()}

        {state.step === 'STAFF' && (
          <>
            <StaffStep
              bootstrap={b}
              selectedServiceId={state.selectedServiceId}
              selectedStaffId={state.selectedStaffId}
              onSelect={selectStaff}
              submitting={state.loading}
            />
            {/* Staff is tap-to-advance — no RunningBar. Inline back-only bar. */}
            <div class="sbw-actionbar">
              <button
                type="button"
                class="sbw-btn sbw-btn--ghost"
                onClick={() =>
                  dispatch({
                    type: 'STEP_SET',
                    value: includeEnhance ? 'ENHANCE' : 'SERVICE',
                  })
                }
              >
                Back
              </button>
            </div>
          </>
        )}

        {state.step === 'TIME' && (
          <>
            <TimeStep
              availableDates={state.availableDates}
              availableSlots={state.availableSlots}
              selectedDate={state.selectedDate}
              selectedSlot={state.selectedSlot}
              timezone={timezone}
              loading={state.loading}
              onSelectDate={selectDate}
              onSelectSlot={selectSlot}
              customViewBundle={customViewBundle}
              isConsult={beYouIsConsult || isConsultService}
            />
            <RunningBar
              summary={state.selectedSlot ? (heldSlotLabel ?? 'Slot reserved') : 'Pick a time'}
              sub={
                state.selectedSlot
                  ? (selectedSvc?.name ?? null)
                  : `${state.availableDates.length} day${state.availableDates.length === 1 ? '' : 's'} available`
              }
              primary="Continue"
              // Be You: the same deposit figure the Service step's button
              // showed, not the full price (2026-10-12 call). The Payment
              // step is where the deposit/pay-in-full choice is made.
              total={
                customViewBundle === 'be-you'
                  ? beYouDepositCents > 0
                    ? beYouDepositCents / 100
                    : undefined
                  : lineTotalDollars
              }
              brand
              disabled={!state.selectedSlot}
              onBack={() =>
                dispatch({
                  type: 'STEP_SET',
                  // PHD Studio: TIME is step 2 (after CONTACT). Other
                  // tenants: TIME follows STAFF/SERVICE/ENHANCE depending
                  // on the upsell + bypassStaffSelection config.
                  value:
                    customViewBundle === 'phd-studio'
                      ? 'CONTACT'
                      : staffStepSkippedFor(state.selectedServiceId)
                        ? includeEnhance
                          ? 'ENHANCE'
                          : 'SERVICE'
                        : 'STAFF',
                })
              }
              submitting={state.loading}
              onContinue={commitTime}
            />
          </>
        )}

        {state.step === 'CONTACT' && (
          <ContactStep
            onSubmit={submitContact}
            submitting={state.loading}
            requireEmail={b.behavior?.requireEmail ?? false}
            requirePhone={b.behavior?.requirePhone ?? true}
            // The CART's client wins over the deep-link prefill: once the
            // customer has submitted their details, the server holds them,
            // and going Back must not present an empty form demanding
            // they be typed again. That re-entry was the funnel's most
            // pointless step — the data was never lost, only unread.
            // Merged PER FIELD, not all-or-nothing: a cart carrying only
            // a phone would otherwise discard a deep-link's email. Cart
            // wins where it has a value, deep-link fills the rest.
            prefilled={{
              firstName: state.cart?.client?.givenName ?? dl?.prefilledContact?.firstName ?? null,
              lastName: state.cart?.client?.familyName ?? dl?.prefilledContact?.lastName ?? null,
              email: state.cart?.client?.email ?? dl?.prefilledContact?.email ?? null,
              phone: state.cart?.client?.phone ?? dl?.prefilledContact?.phone ?? null,
            }}
            // PHD Studio: CONTACT is step 1 — no back button. Other
            // tenants: CONTACT follows TIME so back goes to TIME.
            onBack={
              customViewBundle === 'phd-studio'
                ? undefined
                : () => dispatch({ type: 'STEP_SET', value: 'TIME' })
            }
            customViewBundle={customViewBundle}
          />
        )}

        {/* P5 — the deferred Payment Element, for the one tenant it is
            switched on for. A SEPARATE component beside PaymentStep
            rather than a branch inside it: that file serves Square,
            Boulevard, sb-vault and the Card Element through one state
            machine, and extending it would put all four in the blast
            radius (H22). Same shape BeYouView uses for the Service step.

            The payment is the commitment here: it has already succeeded
            by the time onPaid fires, so this books immediately rather
            than routing through CONFIRM. A confirmation screen AFTER the
            money moved is a screen on which a customer can walk away
            having paid for nothing. */}
        {state.step === 'PAYMENT' && state.deposit && state.cart && paymentElement && (
          <StripePaymentElement
            sessionId={sid}
            publishableKey={paymentElement.publishableKey}
            amountCents={state.deposit.amountCents}
            currency={state.deposit.currency}
            // P5.3 — parity with the Card Element step: the title, the
            // appointment, and the order summary with the deposit-vs-pay-
            // in-full choice and the promo code. Shipped without them, this
            // step took pay-in-full away from Be You, who sells on it.
            header={
              <>
                <DepositHeader
                  cart={state.cart}
                  deposit={state.deposit}
                  isBeYou={customViewBundle === 'be-you'}
                  apptStartAt={state.selectedSlot?.startAt ?? state.cart.lines[0]?.startAt ?? null}
                  timezone={timezone}
                  staffName={
                    state.selectedStaffId
                      ? (b.staff.find((t) => t.id === state.selectedStaffId)?.displayName ?? null)
                      : null
                  }
                />
                <DepositSummary
                  cart={state.cart}
                  deposit={state.deposit}
                  isBeYou={customViewBundle === 'be-you'}
                  disabled={state.loading || paymentConfirming || paymentBusy || peQuotePending}
                  onSetPaymentMode={(payInFull) => guardQuoteChange(() => setPaymentMode(payInFull))}
                  // P5.3 — the providers' own terms for the pay-in-full
                  // amount, only when the server says it qualifies.
                  payInFullHint={
                    state.deposit.financing ? (
                      <FinancingMessage
                        publishableKey={paymentElement.publishableKey}
                        amountCents={state.deposit.financing.amountCents}
                        currency={state.deposit.currency}
                        methods={state.deposit.financing.methods}
                      />
                    ) : undefined
                  }
                  {...(b.discountsEnabled
                    ? {
                        appliedPromoLabel: promoLabel,
                        onApplyPromo: (code: string) => guardQuoteChange(() => applyPromo(code)),
                        onRemovePromo: () => guardQuoteChange(() => removePromo()),
                      }
                    : {})}
                />
              </>
            }
            quoteStale={quoteStale || peQuoteFailed}
            quotePending={peQuotePending}
            onBusyChange={setPaymentBusy}
            onConfirmingChange={setPaymentConfirming}
            // P5.3 — from the quote: card, plus Klarna / Affirm / Afterpay
            // on pay-in-full of $175+. The server pins the intent with the
            // same list.
            paymentMethodTypes={state.deposit.paymentMethodTypes ?? ['card']}
            // D73 — the server's offer and the salon's own wording; the
            // name and email from Contact go on the card so a saved one
            // shows whose it is.
            saveCardOffered={state.deposit.saveCardOffered === true}
            saveCardPolicyText={state.deposit.saveCardPolicyText ?? null}
            billingDetails={{
              name:
                [state.cart.client?.givenName, state.cart.client?.familyName].filter(Boolean).join(' ') ||
                undefined,
              email: state.cart.client?.email || undefined,
            }}
            onCreateIntent={async (saveCard) => {
              const cart = state.cart;
              if (!cart) throw new Error('No cart to pay for');
              // The page they are paying on, so a redirect method (Klarna,
              // Affirm, a bank) can bring them back to it. The server
              // decides whether it is an allowed origin; no returnUrl
              // means a card-only payment, which finishes in place.
              const { clientSecret, returnUrl } = await apiRef.current!.createPaymentIntent(
                cart.id,
                serverAttribution(),
                window.location.href,
                saveCard,
              );
              return { clientSecret, ...(returnUrl ? { returnUrl } : {}) };
            }}
            onPaid={() => {
              // Money has moved. Book now — the server re-reads the
              // intent from Stripe and refuses if it disagrees, so
              // this is a nudge, not a claim to be trusted.
              void confirmBooking();
            }}
            // Keeps the Pay button disabled through the booking that
            // follows; the component cannot see that call itself.
            submitting={state.loading}
            onBack={() =>
              dispatch({
                type: 'STEP_SET',
                value: customViewBundle === 'phd-studio' ? 'TIME' : 'CONTACT',
              })
            }
            // The hold lapsed before the intent was created, so nothing
            // was charged and the Time step is the only control that
            // helps. Without this the server's "please pick a new time"
            // rendered as a red line inside the payment form.
            onCartExpired={(message) => {
              const cart = state.cart;
              if (cart) void recoverToTimeStep(cart.id, message);
            }}
          />
        )}

        {state.step === 'PAYMENT' && state.deposit && state.cart && !paymentElement && (
          <PaymentStep
            deposit={state.deposit}
            cart={state.cart}
            {...(b.discountsEnabled
              ? {
                  appliedPromoLabel: promoLabel,
                  onApplyPromo: applyPromo,
                  onRemovePromo: removePromo,
                }
              : {})}
            apptStartAt={state.selectedSlot?.startAt ?? state.cart.lines[0]?.startAt ?? null}
            timezone={timezone}
            staffName={
              state.selectedStaffId
                ? (b.staff.find((t) => t.id === state.selectedStaffId)?.displayName ?? null)
                : null
            }
            client={{
              givenName: state.cart.client?.givenName,
              familyName: state.cart.client?.familyName,
              email: state.cart.client?.email,
            }}
            // The card frame lives on OUR origin (apiBase), not the salon's
            // page. merchant is the slug the frame's route reads to scope its
            // frame-ancestors CSP.
            frameOrigin={new URL(apiBase).origin}
            merchant={merchant}
            merchantName={businessName}
            onAttached={attachPayment}
            onSetPaymentMode={setPaymentMode}
            quoteStale={quoteStale}
            onBack={() =>
              dispatch({
                type: 'STEP_SET',
                // PHD Studio: PAYMENT is step 3 (after TIME). Other
                // tenants: PAYMENT follows CONTACT.
                value: customViewBundle === 'phd-studio' ? 'TIME' : 'CONTACT',
              })
            }
            submitting={state.loading}
            customViewBundle={customViewBundle}
          />
        )}

        {state.step === 'CONFIRM' && state.cart && (
          <ConfirmStep
            cart={state.cart}
            locationName={locationName}
            timezone={timezone}
            onConfirm={() => confirmBooking()}
            onBack={() =>
              dispatch({
                type: 'STEP_SET',
                value:
                  // includePayment, not "amount > 0": a zero-deposit
                  // tenant offering pay-in-full still HAS a Payment step,
                  // and Back must return to it rather than making the
                  // customer re-enter contact details to change the choice.
                  includePayment ? 'PAYMENT' : 'CONTACT',
              })
            }
            submitting={state.loading}
            // A customer who already paid now LANDS here — that is what
            // skipping the payment step means — and this screen used to
            // show them a total with no hint their money was taken. It
            // also drives the cancellation line, whose deposit-forfeit
            // sentence applies to exactly them.
            //
            // The QUOTED amount, not a sum of everything on the cart:
            // `alreadyPaid` means the server holds a payment for
            // exactly this figure, so this is the money this booking
            // stands on. A cart total would overstate it the moment a
            // second payment existed — and that is the screen where a
            // customer reads what they were charged.
            depositPaidCents={
              state.deposit?.alreadyPaid ? state.deposit.amountCents : undefined
            }
            // Sales tax (Be You only): the total the customer owes, tax in.
            totalCents={
              state.deposit?.fullTaxCents ? state.deposit.fullAmountCents : undefined
            }
            primaryCtaCopy={b.primaryCtaCopy ?? null}
            bookingProfile={b.bookingProfile}
          />
        )}

        {state.step === 'DONE' && state.booking && (
          <DoneStep
            booking={state.booking}
            timezone={timezone}
            allowCustomerCancel={b.bookingProfile.allowCustomerCancel ?? false}
            locationPhone={b.location?.phone}
            confirmationCopy={b.behavior?.confirmationCopy ?? null}
            // D71 — tell the customer, in writing on the screen they keep
            // open, which card the salon will charge and for how much.
            cardOnFile={
              state.attachedCard && state.deposit?.cardOnFile
                ? {
                    last4: state.attachedCard.last4 ?? null,
                    depositCents: state.deposit.amountCents,
                    currency: state.deposit.currency,
                    merchantName: businessName,
                  }
                : null
            }
          />
        )}

        {/* Pay-over-time strip — Be You only, every step except DONE.
            Hoisted out of the SERVICE branch so the runbar's reserved
            37px at the bottom doesn't leave dead space on Time / Contact
            / Payment. Tapping sends the customer to the host page's
            #payment-options financing section (Be You explains Cherry
            there) rather than linking out to the provider directly. In
            overlay mode we dismiss the widget first so
            the host page is actually visible to scroll to. */}
        {/* P5.3 — names the methods Be You now offers in the widget
            (Klarna · Affirm · Afterpay) when it has them; "Cherry" stays
            for a Be You-design tenant without them. Hidden on the Payment
            Element step itself: the options are right there in the form,
            and this link closes the widget to scroll the host page —
            the last thing to do mid-checkout. */}
        {customViewBundle === 'be-you' &&
          state.step !== 'DONE' &&
          // Phase 6 — a Special Services purchase is card-only (D77), and
          // so are the tabs that list them: the bar must not suggest a
          // prescription can be financed.
          !purchase &&
          !(b.shop && state.step === 'SERVICE' && (beYouActiveTab === 'weight' || beYouActiveTab === 'rx')) &&
          !(state.step === 'PAYMENT' && paymentElement) && (
          <a
            class="sbw-pay-options-bar"
            href="#payment-options"
            onClick={(e) => {
              e.preventDefault();
              // Overlay mode covers the page + locks body scroll, so close
              // the widget first; close() is a harmless restore for inline
              // mounts. Defer the scroll a frame so teardown (which restores
              // body overflow) settles before scrollIntoView runs.
              const inOverlay = !!document.querySelector('[data-sb-widget-overlay]');
              if (inOverlay) {
                try {
                  (
                    window as unknown as { SalonLift?: { close?: () => void } }
                  ).SalonLift?.close?.();
                } catch {
                  /* ignore — fall through to the scroll/hash below */
                }
              }
              requestAnimationFrame(() => {
                const target = document.getElementById('payment-options');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  try {
                    history.replaceState(null, '', '#payment-options');
                  } catch {
                    /* ignore — scroll already happened */
                  }
                } else {
                  // Section not on this page — set the hash so a host-page
                  // handler / on-load anchor can pick it up.
                  window.location.hash = 'payment-options';
                }
              });
            }}
          >
            <span class="sbw-pay-options-eyebrow">Pay over time</span>
            <span class="sbw-pay-options-providers">
              {payOverTimeProviders(b.financingMethods)}
            </span>
            <span class="sbw-pay-options-arrow">→</span>
          </a>
        )}
      </div>
    </div>
  );
}

function TrustBlock({ trust }: { trust: BootstrapResponse['trust'] }) {
  if (!trust) return null;
  const rating =
    typeof trust.ratingTenths === 'number' ? (trust.ratingTenths / 10).toFixed(1) : null;
  const pills: Array<{ key: string; node: import('preact').ComponentChildren }> = [];
  if (rating !== null) {
    pills.push({
      key: 'rating',
      node: (
        <>
          <span class="sbw-trust-star" aria-hidden="true">
            ★
          </span>
          {rating}
          {typeof trust.reviewCount === 'number' && trust.reviewCount > 0 && (
            <>
              {' '}
              <span class="sbw-trust-meta">({trust.reviewCount.toLocaleString()})</span>
            </>
          )}
        </>
      ),
    });
  }
  if (typeof trust.recentBookings === 'number' && trust.recentBookings > 0) {
    pills.push({
      key: 'recent',
      node: (
        <>
          <span class="sbw-trust-meta">
            {trust.recentBookings.toLocaleString()} booked this week
          </span>
        </>
      ),
    });
  }
  if (pills.length === 0) return null;
  return (
    <div class="sbw-trust" role="group" aria-label="Reviews and recent bookings">
      {pills.map((p, i) => (
        <span key={p.key} class="sbw-trust-pill">
          {p.node}
          {i < pills.length - 1 && (
            <span class="sbw-trust-sep" aria-hidden="true">
              ·
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

function Progress({
  step,
  includePayment,
  bypassStaff = false,
  includeEnhance = false,
}: {
  step: import('./state').Step;
  includePayment: boolean;
  bypassStaff?: boolean;
  includeEnhance?: boolean;
}) {
  const steps: Array<{ key: import('./state').Step; label: string }> = [
    { key: 'SERVICE', label: 'Service' },
    ...(includeEnhance ? ([{ key: 'ENHANCE', label: 'Enhance' }] as const) : ([] as const)),
    ...(bypassStaff ? ([] as const) : ([{ key: 'STAFF', label: 'Stylist' }] as const)),
    { key: 'TIME', label: 'Time' },
    { key: 'CONTACT', label: 'Info' },
    ...(includePayment ? ([{ key: 'PAYMENT', label: 'Payment' }] as const) : ([] as const)),
    { key: 'CONFIRM', label: 'Confirm' },
  ];
  const currentIdx = steps.findIndex((s) => s.key === step);
  return (
    <ol class="sbw-progress" aria-label="Booking progress">
      {steps.map((s, i) => {
        const isDone = i < currentIdx;
        const isActive = i === currentIdx;
        return (
          <li
            key={s.key}
            class={`sbw-progress-step ${isDone ? 'sbw-progress-done' : ''} ${
              isActive ? 'sbw-progress-active' : ''
            }`}
            aria-current={isActive ? 'step' : undefined}
          >
            <span class="sbw-progress-dot">
              <span class="sbw-progress-dot-num">{i + 1}</span>
            </span>
            <span class="sbw-progress-label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

